/**
 * Size Advisor - Artwork Size Calculator
 * Premium, accessible modal component for product pages
 */

function getTemplateSuffixToken() {
  if (!document.body) return '';

  const tokens = Array.from(document.body.classList);
  return (
    tokens.find((token) =>
      token === 'float-canvas'
      || token.startsWith('float-canvas.')
      || token === 'kids-customized'
      || token.startsWith('kids-customized.')
      || token === 'customized'
      || token.startsWith('customized.')
    ) || ''
  );
}

function getCountryCode() {
  if (window.Shopify && typeof window.Shopify.country === 'string' && window.Shopify.country.length > 0) {
    return window.Shopify.country.toUpperCase();
  }

  const lang = document.documentElement?.lang || '';
  const langParts = lang.toUpperCase().split('-');
  if (langParts.length > 1) {
    return langParts[1];
  }

  return '';
}

function getDefaultSizeUnit() {
  const templateSuffix = getTemplateSuffixToken();
  const isTargetTemplate = templateSuffix !== '';
  const countryCode = getCountryCode();
  const isUsOrUk = countryCode === 'US' || countryCode === 'GB' || countryCode === 'UK';

  if (isTargetTemplate && isUsOrUk) {
    return 'inch';
  }

  return 'cm';
}

function syncCartSizeUnitProperty(unit) {
  const normalizedUnit = unit === 'inch' ? 'inch' : 'cm';
  const addToCartForms = document.querySelectorAll('form[action*="/cart/add"]');

  addToCartForms.forEach((form) => {
    let unitInput = form.querySelector('input[name="properties[_size_unit]"]');

    if (!unitInput) {
      unitInput = document.createElement('input');
      unitInput.type = 'hidden';
      unitInput.name = 'properties[_size_unit]';
      form.appendChild(unitInput);
    }

    unitInput.value = normalizedUnit;
  });
}

let __productSetCountCache = null;

function isUsMarket() {
  return getCountryCode() === 'US';
}

function cmToInches(valueCm) {
  return Math.round(valueCm / 2.54);
}

function formatDualSize(widthCm, heightCm, primaryUnit) {
  const widthIn = cmToInches(widthCm);
  const heightIn = cmToInches(heightCm);

  if (primaryUnit === 'inch') {
    return `${widthIn}x${heightIn} in`;
  }

  return `${widthCm}x${heightCm} cm`;
}

function formatDualLength(lengthCm, primaryUnit) {
  const roundedCm = Math.round(lengthCm);
  const roundedIn = cmToInches(lengthCm);

  if (primaryUnit === 'inch') {
    if (isUsMarket()) {
      return `${roundedIn} in`;
    }
    return `${roundedIn} in (${roundedCm} cm)`;
  }

  return `${roundedCm} cm (${roundedIn} in)`;
}

function parseSizePair(text) {
  if (!text) return null;

  const normalizedText = text.replace(/\s+/g, ' ').trim();
  const cmMatch = normalizedText.match(/(\d+)\s*[x×]\s*(\d+)\s*cm/i);
  if (cmMatch) {
    return {
      width: parseInt(cmMatch[1], 10),
      height: parseInt(cmMatch[2], 10)
    };
  }

  const genericMatch = normalizedText.match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (!genericMatch) return null;

  return {
    width: parseInt(genericMatch[1], 10),
    height: parseInt(genericMatch[2], 10)
  };
}

function detectSetCountFromTitle(title) {
  const lowerTitle = (title || '').toLowerCase();

  const setMatch = lowerTitle.match(/set\s*(?:of\s*)?(\d+)/i);
  if (setMatch) {
    return parseInt(setMatch[1], 10);
  }

  const pieceMatch = lowerTitle.match(/(\d+)[\s-]*piece/i);
  if (pieceMatch) {
    return parseInt(pieceMatch[1], 10);
  }

  if (lowerTitle.includes('duo') || lowerTitle.includes('pair')) {
    return 2;
  }
  if (lowerTitle.includes('trio') || lowerTitle.includes('triptych')) {
    return 3;
  }

  return 1;
}

function getProductSetCount() {
  if (__productSetCountCache !== null) {
    return __productSetCountCache;
  }

  let productTitle = '';
  const dataEl = document.querySelector('[data-size-advisor-data]');
  if (dataEl) {
    try {
      const parsed = JSON.parse(dataEl.textContent);
      productTitle = parsed?.productTitle || '';
    } catch (error) {
      productTitle = '';
    }
  }

  if (!productTitle) {
    const titleEl = document.querySelector('.m-product-title, .product__title, h1');
    productTitle = titleEl ? titleEl.textContent.trim() : '';
  }

  __productSetCountCache = detectSetCountFromTitle(productTitle);
  return __productSetCountCache;
}

function getSizeAdvisorChoiceCopy() {
  const defaults = {
    sizeUseHallway: 'Hallway / small wall',
    sizeUseSideboard: 'Sideboard / small wall',
    sizeUseTwoSeater: '2-seater sofa',
    sizeUseCompactThreeSeater: 'Compact 3-seater',
    sizeUseThreeSeater: '3-seater sofa',
    sizeUseBedOrWideSofa: 'Bed / wide sofa',
    sizeUseStatementWall: 'Statement wall',
    bestForLabel: 'Our advice:',
    sizeUseHallwayHelp: 'a hallway, entryway, smaller wall, or above a compact cabinet.',
    sizeUseSideboardHelp: 'a sideboard, smaller living room wall, or a compact dining nook.',
    sizeUseTwoSeaterHelp: 'a 2-seater sofa or a narrower wall section.',
    sizeUseCompactThreeSeaterHelp: 'a compact 3-seater sofa or a medium wall.',
    sizeUseThreeSeaterHelp: 'a standard 3-seater sofa or a wider console wall.',
    sizeUseBedOrWideSofaHelp: 'a bed, wide sofa, or a larger bedroom wall.',
    sizeUseStatementWallHelp: 'a large wall where the artwork should be the focal point.',
    sizeAdviceWhyPrefix: 'Why this works:',
    sizeAdviceRecommendTitle: 'Why we recommend {{ size }}',
    sizeAdviceWhyHallway: 'This size keeps enough breathing room on compact walls and avoids a crowded look.',
    sizeAdviceWhySideboard: 'This format sits cleanly above a sideboard and keeps nearby styling balanced.',
    sizeAdviceWhyTwoSeater: 'This width aligns naturally with most 2-seater sofas, so the wall feels proportioned.',
    sizeAdviceWhyCompactThreeSeater: 'This size fills a medium wall with presence while still keeping a calm look.',
    sizeAdviceWhyThreeSeaterSofa: 'This is the visual sweet spot for most 3-seater layouts and everyday living rooms.',
    sizeAdviceWhyBedOrWideSofa: 'This size gives enough visual weight above wider furniture so the wall feels complete.',
    sizeAdviceWhyStatementWall: 'This larger format creates a true focal point on bigger walls without feeling lost.',
    sizeAdviceProofSubtle: 'Popular with customers who prefer a refined, minimal wall look.',
    sizeAdviceProofBalanced: 'Most customers pick this range for the safest premium balance in real homes.',
    sizeAdviceProofStatement: 'Popular with customers who want a bolder, gallery-style impact.',
    sizeAdviceProofDefault: 'A trusted choice for a premium wall result with balanced proportions.',
    sizeAdvicePersuasion: 'This size gives a {{ fit }} look and keeps the artwork in proportion with your space.',
    sizeAdviceMostChosen: 'It is one of the most selected sizes for a balanced, premium wall look.',
    widthHeight: 'W × H',
    hybridRecommendedTitle: 'Quick picks',
    hybridViewAllSizes: 'See more sizes',
    hybridAllSizesTitle: 'More sizes',
    hybridSelectedLabel: 'Selected:',
    hybridSelectedTag: 'SELECTED',
    hybridSelectSize: 'Select',
    fitSubtle: 'Subtle fit',
    fitBalanced: 'Balanced fit',
    fitStatement: 'Statement fit',
    hybridGroupSmall: 'Smaller spaces',
    hybridGroupBalanced: 'Sofa and sideboard fit',
    hybridGroupLarge: 'Larger walls and beds'
  };

  const dataEl = document.querySelector('[data-size-advisor-data]');
  if (!dataEl) return defaults;

  try {
    const parsed = JSON.parse(dataEl.textContent);
    return {
      ...defaults,
      ...(parsed?.translations || {})
    };
  } catch (error) {
    return defaults;
  }
}

function getSizeDescriptorKey(size, sizes) {
  if (!size || !sizes.length) return '';

  const index = sizes.findIndex((entry) => entry.width === size.width && entry.height === size.height);
  if (index === -1) return '';
  const total = sizes.length;

  if (total === 1) return 'threeSeaterSofa';
  if (total === 2) return index === 0 ? 'twoSeater' : 'bedOrWideSofa';
  if (total === 3) {
    return ['hallway', 'threeSeaterSofa', 'bedOrWideSofa'][index] || 'threeSeaterSofa';
  }
  if (total === 4) {
    return ['hallway', 'twoSeater', 'threeSeaterSofa', 'bedOrWideSofa'][index] || 'threeSeaterSofa';
  }
  if (total === 5) {
    return ['hallway', 'twoSeater', 'threeSeaterSofa', 'bedOrWideSofa', 'statementWall'][index] || 'threeSeaterSofa';
  }
  if (total === 6) {
    return ['hallway', 'sideboard', 'twoSeater', 'threeSeaterSofa', 'bedOrWideSofa', 'statementWall'][index] || 'threeSeaterSofa';
  }

  if (index === 0) return 'hallway';
  if (index === 1) return 'sideboard';
  if (index === 2) return 'twoSeater';
  if (index === 3) return 'compactThreeSeater';
  if (index === total - 2) return 'bedOrWideSofa';
  if (index === total - 1) return 'statementWall';
  return 'threeSeaterSofa';
}

function getSizeDescriptorLabel(descriptorKey, translations) {
  if (descriptorKey === 'hallway') return translations.sizeUseHallway;
  if (descriptorKey === 'sideboard') return translations.sizeUseSideboard;
  if (descriptorKey === 'twoSeater') return translations.sizeUseTwoSeater;
  if (descriptorKey === 'compactThreeSeater') return translations.sizeUseCompactThreeSeater;
  if (descriptorKey === 'threeSeaterSofa') return translations.sizeUseThreeSeater;
  if (descriptorKey === 'bedOrWideSofa') return translations.sizeUseBedOrWideSofa;
  if (descriptorKey === 'statementWall') return translations.sizeUseStatementWall;
  return '';
}

function getSizeDescriptorHelp(descriptorKey, translations) {
  if (descriptorKey === 'hallway') return translations.sizeUseHallwayHelp;
  if (descriptorKey === 'sideboard') return translations.sizeUseSideboardHelp;
  if (descriptorKey === 'twoSeater') return translations.sizeUseTwoSeaterHelp;
  if (descriptorKey === 'compactThreeSeater') return translations.sizeUseCompactThreeSeaterHelp;
  if (descriptorKey === 'threeSeaterSofa') return translations.sizeUseThreeSeaterHelp;
  if (descriptorKey === 'bedOrWideSofa') return translations.sizeUseBedOrWideSofaHelp;
  if (descriptorKey === 'statementWall') return translations.sizeUseStatementWallHelp;
  return '';
}

function getDescriptorAdviceWhy(descriptorKey, translations) {
  if (descriptorKey === 'hallway') return translations.sizeAdviceWhyHallway;
  if (descriptorKey === 'sideboard') return translations.sizeAdviceWhySideboard;
  if (descriptorKey === 'twoSeater') return translations.sizeAdviceWhyTwoSeater;
  if (descriptorKey === 'compactThreeSeater') return translations.sizeAdviceWhyCompactThreeSeater;
  if (descriptorKey === 'threeSeaterSofa') return translations.sizeAdviceWhyThreeSeaterSofa;
  if (descriptorKey === 'bedOrWideSofa') return translations.sizeAdviceWhyBedOrWideSofa;
  if (descriptorKey === 'statementWall') return translations.sizeAdviceWhyStatementWall;
  return '';
}

function getSizeAdviceProof(fitKey, translations) {
  if (fitKey === 'subtle') return translations.sizeAdviceProofSubtle;
  if (fitKey === 'statement') return translations.sizeAdviceProofStatement;
  if (fitKey === 'balanced') return translations.sizeAdviceProofBalanced;
  return translations.sizeAdviceProofDefault || translations.sizeAdviceMostChosen || '';
}

function reorderProductSupportBlock() {
  const supportLink = document.querySelector('.whatsapp-help-link');
  if (!supportLink) return;

  const supportBlock = supportLink.closest('.main-product__block');
  if (!supportBlock || !supportBlock.parentNode) return;

  const container = supportBlock.parentNode;
  const blocks = Array.from(container.children).filter((el) => el.classList.contains('main-product__block'));

  const paymentBlock = blocks.find((block) => block.querySelector('.payment-box-2'));
  if (paymentBlock) {
    const desiredSpot = paymentBlock.nextElementSibling;
    if (desiredSpot !== supportBlock) {
      container.insertBefore(supportBlock, desiredSpot);
    }
    return;
  }

  const firstUspRow = document.querySelector('.USP-product-mini, .USP-product');
  if (!firstUspRow) return;

  const firstUspBlock = firstUspRow.closest('.main-product__block');
  if (!firstUspBlock || firstUspBlock === supportBlock) return;
  if (firstUspBlock.parentNode !== supportBlock.parentNode) return;

  supportBlock.parentNode.insertBefore(supportBlock, firstUspBlock);
}

function enhanceWhatsAppHelpLink() {
  const helpLinks = document.querySelectorAll('.whatsapp-help-link');
  if (!helpLinks.length) return;

  helpLinks.forEach((link) => {
    if (link.dataset.enhancedSupport === 'true') return;

    const content = link.querySelector('.whatsapp-help-link__content');
    const rightIcon = link.querySelector('.whatsapp-help-link__arrow');


    const title = content.querySelector('.whatsapp-help-link__title');
    if (title) {
      title.textContent = 'Free personal wall consultation';
    }

    const subtitle = content.querySelector('.whatsapp-help-link__subtitle');
    if (subtitle) {
      subtitle.textContent = 'Send us a photo on WhatsApp. Our team will recommend the ideal artwork, size and frame.';
    }

    let meta = content.querySelector('.whatsapp-help-link__meta');
    if (!meta) {
      meta = document.createElement('span');
      meta.className = 'whatsapp-help-link__meta';
      content.appendChild(meta);
    }
   

    rightIcon.innerHTML = `
      <span class="whatsapp-help-link__cta">Chat</span>
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M12.04 2C6.55 2 2.1 6.45 2.1 11.94c0 1.76.46 3.48 1.33 4.99L2 22l5.19-1.36a9.9 9.9 0 0 0 4.85 1.24h.01c5.48 0 9.93-4.45 9.93-9.94A9.93 9.93 0 0 0 12.04 2Zm5.82 14.03c-.25.7-1.46 1.33-2.02 1.42-.52.08-1.2.12-1.93-.12-.45-.15-1.03-.33-1.78-.65-3.14-1.36-5.18-4.52-5.34-4.73-.17-.21-1.28-1.7-1.28-3.24 0-1.54.81-2.3 1.1-2.62.29-.31.63-.39.84-.39.21 0 .42 0 .61.01.19.01.44-.07.69.52.25.6.85 2.08.93 2.23.08.15.13.33.02.53-.1.21-.15.33-.31.5-.15.17-.32.38-.46.5-.15.12-.3.26-.13.5.17.25.74 1.22 1.58 1.97 1.08.96 1.99 1.26 2.27 1.4.28.14.45.12.62-.08.17-.21.71-.82.9-1.11.19-.29.38-.24.65-.14.27.1 1.71.8 2 .95.29.14.48.21.55.33.07.12.07.68-.18 1.38Z"/>
      </svg>
    `;

    link.dataset.enhancedSupport = 'true';
  });
}

function enhanceTrustBoxAvatars() {
  const trustBoxes = document.querySelectorAll('.trust-box');
  if (!trustBoxes.length) return;

  trustBoxes.forEach((box) => {
    if (box.dataset.enhancedAvatars === 'true') return;

    const sourceImage = box.querySelector('.trust-image, img');
    const wrapper = box.querySelector('.trust-box-wrapper');
    if (!sourceImage || !wrapper) return;

    wrapper.querySelectorAll('p').forEach((paragraph) => {
      if (paragraph.textContent.trim() === '') {
        paragraph.remove();
      }
    });

    const avatarSrc = sourceImage.getAttribute('src');
    if (!avatarSrc) return;

    const influencers = [
      {
        handle: '@z1nebbb',
        src: avatarSrc
      },
      {
        handle: '@Youstoub',
        src: 'https://cdn.shopify.com/s/files/1/0869/8973/4181/files/393590347_700339458310700_6016269705379405608_n.jpg?v=1783349847'
      },
      {
        handle: '@Najah.Strength',
        src: 'https://cdn.shopify.com/s/files/1/0869/8973/4181/files/623835984_17995709756885535_1266993463379853586_n.jpg?v=1783349847'
      }
    ];

    const avatars = document.createElement('div');
    avatars.className = 'trust-avatars';

    influencers.forEach((influencer) => {
      const avatar = document.createElement('div');
      avatar.className = 'trust-avatar';

      const image = document.createElement('img');
      image.src = influencer.src;
      image.alt = `${influencer.handle} verified profile`;
      image.loading = 'lazy';
      image.decoding = 'async';

      const badge = document.createElement('span');
      badge.className = 'trust-avatar__verified';
      badge.setAttribute('aria-hidden', 'true');
      badge.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="12" fill="#1D9BF0"/>
          <path d="M7 12.2L10.1 15.3L17 8.4" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      avatar.appendChild(image);
      avatar.appendChild(badge);
      avatars.appendChild(avatar);
    });

    sourceImage.remove();
    box.insertBefore(avatars, wrapper);
    box.classList.add('trust-box--enhanced');
    box.dataset.enhancedAvatars = 'true';
  });
}

function forceDynamicCheckoutVisibility() {
  const wrappers = document.querySelectorAll("[data-shopify='payment-button'], [data-shopify=\"payment-button\"], [data-shopify=payment-button]");
  wrappers.forEach((wrapper) => {
    wrapper.style.setProperty('display', 'block', 'important');
    wrapper.style.setProperty('visibility', 'visible', 'important');
    wrapper.style.setProperty('opacity', '1', 'important');
    wrapper.style.setProperty('height', 'auto', 'important');
  });

  const paymentButtons = document.querySelectorAll('.shopify-payment-button');
  paymentButtons.forEach((button) => {
    button.style.setProperty('display', 'block', 'important');
    button.style.setProperty('visibility', 'visible', 'important');
    button.style.setProperty('opacity', '1', 'important');
  });
}

function optimizeUsShippingDeliveryCopy() {
  if (!isUsMarket()) return;

  const shippingLabels = document.querySelectorAll('.m-product-shipping--content strong');
  shippingLabels.forEach((label) => {
    const current = (label.textContent || '').trim().toLowerCase();
    if (
      current.includes('order today')
      || current.includes('ordered today')
      || current.includes('free delivery on')
    ) {
      label.textContent = 'Free shipping to the US, estimated delivery:';
    }
  });
}

function addFrameReadyToHangNote() {
  const optionContainers = document.querySelectorAll('.m-product-option');
  optionContainers.forEach((container) => {
    const labelContainer = container.querySelector('.m-product-option--label');
    const optionContent = container.querySelector('.m-product-option--content');
    if (!labelContainer) return;
    if (!optionContent) return;

    const labelText = (labelContainer.textContent || '').toLowerCase();
    if (!labelText.includes('frame')) return;

    let note = container.querySelector('.frame-ready-note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'frame-ready-note';
    }
  });
}

function initProductLayoutEnhancements() {
  forceDynamicCheckoutVisibility();
  optimizeUsShippingDeliveryCopy();
  addFrameReadyToHangNote();
  enhanceWhatsAppHelpLink();
  reorderProductSupportBlock();
  enhanceTrustBoxAvatars();
  initMobileStickyAddToCartGate();
}

let __mobileStickyAtcGateBound = false;
let __mobileStickyAtcGateTicking = false;
let __mobileStickyAtcGateContext = {
  stickyBars: [],
  buySection: null,
  mobileQuery: null
};

function updateMobileStickyAtcGateState() {
  const { stickyBars, buySection, mobileQuery } = __mobileStickyAtcGateContext;
  if (!stickyBars.length || !buySection || !mobileQuery) return;

  if (!mobileQuery.matches) {
    stickyBars.forEach((bar) => {
      bar.classList.remove('is-gated-hidden');
    });
    return;
  }

  const sectionRect = buySection.getBoundingClientRect();
  const hasPassedPrimaryBuySection = sectionRect.bottom <= 0;

  stickyBars.forEach((bar) => {
    bar.classList.toggle('is-gated-hidden', !hasPassedPrimaryBuySection);
  });
}

function scheduleMobileStickyAtcGateUpdate() {
  if (__mobileStickyAtcGateTicking) return;
  __mobileStickyAtcGateTicking = true;

  requestAnimationFrame(() => {
    __mobileStickyAtcGateTicking = false;
    updateMobileStickyAtcGateState();
  });
}

function initMobileStickyAddToCartGate() {
  const stickyBars = Array.from(document.querySelectorAll('.m-sticky-addtocart[data-show-on-mobile="true"]'));
  if (!stickyBars.length) return;

  const buySection = document.querySelector('.main-product__block-buy_buttons, .m-main-product--info .m-product-form--main');
  if (!buySection) return;

  const mobileQuery = __mobileStickyAtcGateContext.mobileQuery || window.matchMedia('(max-width: 767px)');
  __mobileStickyAtcGateContext = {
    stickyBars,
    buySection,
    mobileQuery
  };

  updateMobileStickyAtcGateState();

  if (__mobileStickyAtcGateBound) return;
  __mobileStickyAtcGateBound = true;

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', scheduleMobileStickyAtcGateUpdate);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(scheduleMobileStickyAtcGateUpdate);
  }

  window.addEventListener('scroll', scheduleMobileStickyAtcGateUpdate, { passive: true });
  window.addEventListener('orientationchange', scheduleMobileStickyAtcGateUpdate);
  window.addEventListener('resize', scheduleMobileStickyAtcGateUpdate);
}

class SizeAdvisor {
  constructor(container) {
    this.container = container;
    this.optionContainer = container.closest('.m-product-option');
    this.triggerWrap = container.querySelector('[data-size-advisor-trigger-wrap]');
    this.trigger = container.querySelector('[data-size-advisor-trigger]');
    this.triggerContext = container.querySelector('[data-size-advisor-context]');
    this.triggerTitle = container.querySelector('[data-size-advisor-trigger-title]');
    this.triggerSubtitle = container.querySelector('[data-size-advisor-trigger-subtitle]');
    this.triggerDefaultTitle = this.triggerTitle ? this.triggerTitle.textContent.trim() : '';
    this.triggerDefaultSubtitle = this.triggerSubtitle ? this.triggerSubtitle.textContent.trim() : '';
    this.modal = container.querySelector('[data-size-advisor-modal]');

    if (this.modal && this.modal.parentNode !== document.body) {
      document.body.appendChild(this.modal);
    }

    this.closeBtn = this.modal ? this.modal.querySelector('[data-size-advisor-close]') : null;
    this.form = this.modal ? this.modal.querySelector('[data-size-advisor-form]') : null;
    this.wallPanel = this.modal ? this.modal.querySelector('[data-size-advisor-panel="wall"]') : null;
    this.furniturePanel = this.modal ? this.modal.querySelector('[data-size-advisor-panel="furniture"]') : null;
    this.wallInput = this.modal ? this.modal.querySelector('[data-size-advisor-wall]') : null;
    this.sofaInput = this.modal ? this.modal.querySelector('[data-size-advisor-sofa]') : null;
    this.calculateBtn = this.modal ? this.modal.querySelector('[data-size-advisor-calculate]') : null;
    this.resultsContainer = this.modal ? this.modal.querySelector('[data-size-advisor-results]') : null;
    this.resultsList = this.modal ? this.modal.querySelector('[data-size-advisor-results-list]') : null;
    this.whatsappLink = this.modal ? this.modal.querySelector('[data-size-advisor-whatsapp]') : null;
    this.summaryContainer = this.modal ? this.modal.querySelector('[data-size-advisor-summary]') : null;
    this.step1 = this.modal ? this.modal.querySelector('[data-step="1"]') : null;
    this.step2 = this.modal ? this.modal.querySelector('[data-step="2"]') : null;
    this.step3 = this.modal ? this.modal.querySelector('[data-step="3"]') : null;
    this.backBtn = this.modal ? this.modal.querySelector('[data-size-advisor-back]') : null;
    this.backToPlacementBtn = this.modal ? this.modal.querySelector('[data-size-advisor-back-to-placement]') : null;
    this.step2Title = this.modal ? this.modal.querySelector('[data-size-advisor-step2-title]') : null;
    this.step2Intro = this.modal ? this.modal.querySelector('[data-size-advisor-step2-intro]') : null;
    this.placementBtns = this.modal ? this.modal.querySelectorAll('[data-size-advisor-placement]') : [];
    this.quickSelectBtns = this.modal ? this.modal.querySelectorAll('[data-sofa-preset]') : [];
    this.showExactBtn = this.modal ? this.modal.querySelector('[data-size-advisor-show-exact]') : null;
    this.exactWrapper = this.modal ? this.modal.querySelector('[data-size-advisor-exact-wrapper]') : null;
    this.sofaPresetGroup = this.modal ? this.modal.querySelector('[data-size-advisor-sofa-presets]') : null;
    this.furnitureLabel = this.modal ? this.modal.querySelector('[data-size-advisor-furniture-label]') : null;
    this.furnitureHint = this.modal ? this.modal.querySelector('[data-size-advisor-furniture-hint]') : null;

    this.variants = [];
    this.productTitle = '';
    this.moneyFormat = '€{{amount}}';
    this.whatsappNumber = '31203086015';
    this.setCount = 1;
    this.currentPlacement = null;
    this.focusableElements = null;
    this.firstFocusable = null;
    this.lastFocusable = null;
    this.previousActiveElement = null;
    this.boundHandleTabKey = this.handleTabKey.bind(this);
    this.variantSwitchCount = 0;
    this.autoPromptThreshold = 3;
    this.autoPromptShown = false;
    this.suppressNextSizeChange = false;
    this.recommendationMap = {};
    this.selectedRecommendation = null;

    this.translations = {
      noResults: "We couldn't find a perfect match for your space. Send us a photo on WhatsApp and we'll help you personally!",
      triggerTitle: 'Find your ideal size',
      triggerSubtitle: 'Get a clear recommendation for your space in 10 seconds.',
      autoPromptTitle: 'Not sure which size suits your space?',
      autoPromptBody: 'Get a personal recommendation in 10 seconds.',
      sofaWidthLabel: 'How wide is your sofa?',
      sofaWidthHint: 'Choose the closest sofa type, or enter the exact width if you know it.',
      bedWidthLabel: 'How wide is your bed?',
      bedWidthHint: 'An approximate width is enough. We only need one measurement to recommend the best fit.',
      sideboardWidthLabel: 'How wide is your sideboard?',
      sideboardWidthHint: 'An approximate width is enough. We only need one measurement to recommend the best fit.',
      summaryPlacement: 'Placement',
      summaryWidth: 'Width',
      setOf: 'Set of',
      sizePerPiece: 'size per piece, price for complete set',
      recommendedHeading: 'Your recommended size',
      balancedFit: 'Balanced fit',
      subtleLook: 'Subtle',
      statementLook: 'Statement',
      recommendedFor: 'Recommended for your',
      fitExplanation: 'The artwork will span approximately {{ percent }} of its width, creating a balanced and refined look.',
      wallFitExplanation: 'The artwork will use approximately {{ percent }} of your available wall width, creating a balanced and refined look.',
      chooseRecommended: 'Choose recommended size',
      viewSize: 'View',
      seeOtherLooks: 'See other looks',
      preferDifferentLook: 'Prefer a different look?',
      shippingBenefits: 'Free worldwide shipping · Ready to hang · 30-day returns',
      threePaymentsOf: 'or 3 payments of',
      artworkSpan: 'Artwork span',
      ofReferenceWidth: 'of {{ reference }} width',
      referenceSofa: 'sofa',
      referenceBed: 'bed',
      referenceSideboard: 'sideboard',
      referenceWall: 'wall',
      pdpRecommendation: 'Recommended for your {{ reference }}',
      ourPick: 'Our pick',
      from: 'from',
      soldOut: 'Sold out',
      select: 'Select',
      widthHeight: 'W × H'
    };

    try {
      const dataEl = container.querySelector('[data-size-advisor-data]');
      if (dataEl) {
        const data = JSON.parse(dataEl.textContent);
        this.variants = data.variants || [];
        this.productTitle = data.productTitle || '';
        this.moneyFormat = data.moneyFormat || this.moneyFormat;
        if (data.translations) {
          this.translations = { ...this.translations, ...data.translations };
        }
        this.setCount = this.detectSetCount(this.productTitle);
      }
    } catch (error) {
      console.error('Size Advisor: Error parsing variant data', error);
    }

    this.unitBtns = this.modal ? this.modal.querySelectorAll('[data-unit]') : [];
    this.unitLabels = this.modal ? this.modal.querySelectorAll('[data-unit-label]') : [];
    this.unitSuffixes = this.modal ? this.modal.querySelectorAll('[data-unit-suffix]') : [];
    this.defaultUnit = getDefaultSizeUnit();
    this.currentUnit = this.defaultUnit;
    this.sizeVariants = this.parseVariantSizes();
    this.lastSelectedSizeKey = this.getCurrentSelectedSizeKey();

    this.positionTrigger();
    this.bindEvents();
    this.setTriggerNudgeState(false);
    this.switchUnit(this.defaultUnit);
    this.validateForm();
  }

  detectSetCount(title) {
    return detectSetCountFromTitle(title);
  }

  switchUnit(unit) {
    const oldUnit = this.currentUnit;
    this.currentUnit = unit;

    this.unitBtns.forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.unit === unit);
    });

    const unitText = unit === 'cm' ? '(cm)' : '(inch)';
    const suffixText = unit === 'cm' ? 'cm' : 'inch';

    this.unitLabels.forEach((label) => {
      label.textContent = unitText;
    });

    this.unitSuffixes.forEach((suffix) => {
      suffix.textContent = suffixText;
    });

    if (this.wallInput && this.wallInput.value) {
      this.wallInput.value = this.convertValue(parseFloat(this.wallInput.value), oldUnit, unit);
    }

    if (this.sofaInput && this.sofaInput.value) {
      this.sofaInput.value = this.convertValue(parseFloat(this.sofaInput.value), oldUnit, unit);
    }

    this.updateInputConstraints();
    syncCartSizeUnitProperty(unit);
  }

  convertValue(value, fromUnit, toUnit) {
    if (fromUnit === toUnit || Number.isNaN(value)) return Math.round(value);

    if (fromUnit === 'cm' && toUnit === 'inch') {
      return Math.round(value / 2.54);
    }

    if (fromUnit === 'inch' && toUnit === 'cm') {
      return Math.round(value * 2.54);
    }

    return Math.round(value);
  }

  parseVariantSizes() {
    const styleKey = getSelectedStyleKey(this.variants);

    return buildSizeVariantCatalog(this.variants, styleKey).map((entry) => ({
      id: entry.id,
      title: entry.key,
      width: entry.width,
      height: entry.height,
      price: entry.price,
      available: entry.available,
      formattedPrice: this.formatMoney(entry.price)
    }));
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const value = (parseInt(cents, 10) / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', Math.round(value))
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
  }

  positionTrigger() {
    if (!this.optionContainer || !this.triggerWrap) return;

    const labelBlock = this.optionContainer.querySelector('.m-product-option--label');
    if (labelBlock) {
      labelBlock.appendChild(this.triggerWrap);
    }
  }

  bindEvents() {
    if (this.trigger) {
      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) {
          this.close();
        }
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.calculate();
      });
    }

    if (this.calculateBtn) {
      this.calculateBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.calculate();
      });
    }

    this.placementBtns.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.setPlacement(btn.dataset.sizeAdvisorPlacement);
      });
    });

    if (this.showExactBtn) {
      this.showExactBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.showExactInput();
      });
    }

    this.quickSelectBtns.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const preset = this.currentUnit === 'inch' ? btn.dataset.sofaPresetInch : btn.dataset.sofaPreset;
        if (!preset || !this.sofaInput) return;

        this.sofaInput.value = preset;
        this.quickSelectBtns.forEach((item) => item.classList.remove('is-active'));
        btn.classList.add('is-active');

        if (this.exactWrapper) {
          this.exactWrapper.hidden = true;
        }

        this.validateForm();
      });
    });

    if (this.wallInput) {
      this.wallInput.addEventListener('input', () => this.validateForm());
    }

    if (this.sofaInput) {
      this.sofaInput.addEventListener('input', () => {
        this.quickSelectBtns.forEach((btn) => btn.classList.remove('is-active'));
        this.validateForm();
      });
    }

    this.unitBtns.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const unit = btn.dataset.unit;
        if (unit && unit !== this.currentUnit) {
          this.switchUnit(unit);
        }
      });
    });

    if (this.resultsList) {
      this.resultsList.addEventListener('click', (event) => {
        const selectBtn = event.target.closest('[data-size-advisor-select]');
        if (!selectBtn || selectBtn.disabled) return;
        this.selectVariant(selectBtn.dataset.sizeAdvisorSelect);
      });
    }

    if (this.backBtn) {
      this.backBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.showStep(2);
      });
    }

    if (this.backToPlacementBtn) {
      this.backToPlacementBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.showStep(1);
      });
    }

    if (this.optionContainer) {
      this.optionContainer.addEventListener('change', () => this.handleProductOptionChange());
      this.optionContainer.addEventListener('click', (event) => {
        if (event.target.closest('[data-custom-select-option]')) {
          setTimeout(() => this.handleProductOptionChange(), 50);
        }
      });
    }
  }

  isOpen() {
    return this.modal && this.modal.hasAttribute('open');
  }

  open() {
    if (!this.modal) return;

    this.sizeVariants = this.parseVariantSizes();

    this.previousActiveElement = document.activeElement;
    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this.showStep(1);
    this.setupFocusTrap();

    setTimeout(() => {
      const activePlacement = Array.from(this.placementBtns).find((btn) => btn.classList.contains('is-active'));
      if (activePlacement) {
        activePlacement.focus();
        return;
      }

      if (this.placementBtns.length) {
        this.placementBtns[0].focus();
      }
    }, 100);
  }

  close() {
    if (!this.modal) return;

    this.modal.removeAttribute('open');
    document.body.style.overflow = '';
    this.modal.removeEventListener('keydown', this.boundHandleTabKey);

    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }

  setupFocusTrap() {
    const content = this.modal ? this.modal.querySelector('.size-advisor-modal__content') : null;
    if (!content) return;

    this.focusableElements = content.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (this.focusableElements.length === 0) return;

    this.firstFocusable = this.focusableElements[0];
    this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    this.modal.removeEventListener('keydown', this.boundHandleTabKey);
    this.modal.addEventListener('keydown', this.boundHandleTabKey);
  }

  handleTabKey(event) {
    if (event.key !== 'Tab') return;

    if (event.shiftKey && document.activeElement === this.firstFocusable) {
      event.preventDefault();
      this.lastFocusable.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === this.lastFocusable) {
      event.preventDefault();
      this.firstFocusable.focus();
    }
  }

  setPlacement(placement, options = {}) {
    const previousPlacement = this.currentPlacement;
    this.currentPlacement = placement;

    this.placementBtns.forEach((btn) => {
      const isActive = btn.dataset.sizeAdvisorPlacement === placement;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (previousPlacement !== placement && !options.preserveValues) {
      if (this.wallInput) this.wallInput.value = '';
      if (this.sofaInput) this.sofaInput.value = '';
      this.quickSelectBtns.forEach((btn) => btn.classList.remove('is-active'));
    }

    if (this.wallPanel) {
      this.wallPanel.hidden = placement !== 'wall';
    }

    if (this.furniturePanel) {
      this.furniturePanel.hidden = !this.isFurniturePlacement(placement);
    }

    if (this.isFurniturePlacement(placement)) {
      const furnitureCopy = this.getFurnitureCopy(placement);
      if (this.furnitureLabel) {
        this.furnitureLabel.textContent = furnitureCopy.label;
      }
      if (this.furnitureHint) {
        this.furnitureHint.textContent = furnitureCopy.hint;
      }
      if (this.step2Title) {
        this.step2Title.textContent = furnitureCopy.label;
      }
      if (this.step2Intro) {
        this.step2Intro.textContent = furnitureCopy.hint;
      }

      if (this.sofaPresetGroup) {
        this.sofaPresetGroup.hidden = placement !== 'sofa';
      }

      if (this.exactWrapper) {
        this.exactWrapper.hidden = placement === 'sofa';
      }
    } else {
      const wallLabelText = this.wallPanel?.querySelector('.size-advisor-field__label')?.textContent?.replace(/\(.*?\)/, '').trim() || this.translations.summaryWidth;
      const wallHintText = this.wallPanel?.querySelector('.size-advisor-field__hint')?.textContent?.trim() || '';
      if (this.step2Title) {
        this.step2Title.textContent = wallLabelText;
      }
      if (this.step2Intro) {
        this.step2Intro.textContent = wallHintText;
      }
    }

    this.updateInputConstraints();
    this.validateForm();
    this.showStep(2);

    if (!options.silent) {
      setTimeout(() => {
        if (placement === 'wall' && this.wallInput) {
          this.wallInput.focus();
          return;
        }

        if (placement === 'sofa' && this.quickSelectBtns.length) {
          this.quickSelectBtns[0].focus();
          return;
        }

        if (this.isFurniturePlacement(placement) && this.sofaInput) {
          this.showExactInput();
        }
      }, 50);
    }
  }

  showExactInput() {
    if (this.exactWrapper) {
      this.exactWrapper.hidden = false;
    }

    this.quickSelectBtns.forEach((btn) => btn.classList.remove('is-active'));

    if (this.sofaInput) {
      this.sofaInput.focus();
    }
  }

  getFurnitureCopy(placement) {
    if (placement === 'bed') {
      return {
        label: this.translations.bedWidthLabel,
        hint: this.translations.bedWidthHint
      };
    }

    if (placement === 'sideboard') {
      return {
        label: this.translations.sideboardWidthLabel,
        hint: this.translations.sideboardWidthHint
      };
    }

    return {
      label: this.translations.sofaWidthLabel,
      hint: this.translations.sofaWidthHint
    };
  }

  updateInputConstraints() {
    const isInch = this.currentUnit === 'inch';

    if (this.wallInput) {
      this.wallInput.min = isInch ? '20' : '50';
      this.wallInput.max = isInch ? '400' : '1000';
      this.wallInput.placeholder = isInch ? '120' : '300';
    }

    if (this.sofaInput) {
      this.sofaInput.min = isInch ? '20' : '50';
      this.sofaInput.max = isInch ? '200' : '500';

      if (this.currentPlacement === 'bed') {
        this.sofaInput.placeholder = isInch ? '71' : '180';
      } else if (this.currentPlacement === 'sideboard') {
        this.sofaInput.placeholder = isInch ? '63' : '160';
      } else {
        this.sofaInput.placeholder = isInch ? '80' : '200';
      }
    }
  }

  validateForm() {
    let isValid = false;

    if (this.currentPlacement === 'wall') {
      const wallValue = this.wallInput ? this.wallInput.value.trim() : '';
      isValid = wallValue !== '' && !Number.isNaN(parseFloat(wallValue)) && parseFloat(wallValue) > 0;
    } else if (this.isFurniturePlacement(this.currentPlacement)) {
      const furnitureValue = this.sofaInput ? this.sofaInput.value.trim() : '';
      isValid = furnitureValue !== '' && !Number.isNaN(parseFloat(furnitureValue)) && parseFloat(furnitureValue) > 0;
    }

    if (this.calculateBtn) {
      this.calculateBtn.disabled = !isValid;
    }

    return isValid;
  }

  calculate() {
    if (!this.validateForm()) return;

    const context = this.buildContext();
    const targetRatio = context.placement === 'wall' ? 0.6 : 0.63;
    const gapBetweenPieces = 8;
    const recommendedTotalWidth = context.referenceWidth * targetRatio;

    let recommendedWidth = recommendedTotalWidth;
    if (this.setCount > 1) {
      const totalGaps = gapBetweenPieces * (this.setCount - 1);
      recommendedWidth = (recommendedTotalWidth - totalGaps) / this.setCount;
    }

    const recommendations = this.findRecommendations(recommendedWidth);
    this.renderResults(recommendations, context);
    this.updateWhatsAppLink(context);
    this.updateSummary(context);
    this.showStep(3);
  }

  buildContext() {
    const isWallPlacement = this.currentPlacement === 'wall';
    const rawValue = isWallPlacement ? parseFloat(this.wallInput.value) : parseFloat(this.sofaInput.value);
    const referenceWidth = this.currentUnit === 'inch' ? rawValue * 2.54 : rawValue;

    return {
      placement: this.currentPlacement,
      referenceWidth,
      referenceDisplay: formatDualLength(referenceWidth, this.currentUnit),
      referenceLabel: this.getReferenceLabel(this.currentPlacement),
      placementLabel: this.getPlacementLabel(this.currentPlacement)
    };
  }

  showStep(stepNumber) {
    if (stepNumber === 1) {
      if (this.step1) this.step1.hidden = false;
      if (this.step2) this.step2.hidden = true;
      if (this.step3) this.step3.hidden = true;
      return;
    }

    if (stepNumber === 2) {
      if (this.step1) this.step1.hidden = true;
      if (this.step2) this.step2.hidden = false;
      if (this.step3) this.step3.hidden = true;

      setTimeout(() => {
        if (this.currentPlacement === 'sofa') {
          const firstPreset = Array.from(this.quickSelectBtns).find((btn) => btn.offsetParent !== null);
          if (firstPreset) {
            firstPreset.focus();
            return;
          }
        }

        if (this.currentPlacement === 'wall' && this.wallInput) {
          this.wallInput.focus();
          return;
        }

        if (this.sofaInput) {
          this.sofaInput.focus();
        }
      }, 100);
      return;
    }

    if (this.step1) this.step1.hidden = true;
    if (this.step2) this.step2.hidden = true;
    if (this.step3) this.step3.hidden = false;

    setTimeout(() => {
      if (this.backBtn) {
        this.backBtn.focus();
      }
    }, 100);
  }

  updateSummary(context) {
    if (!this.summaryContainer) return;

    this.summaryContainer.innerHTML = `
      <span class="size-advisor-summary__item">
        <span class="size-advisor-summary__label">${this.translations.summaryPlacement}:</span>
        ${context.placementLabel}
      </span>
      <span class="size-advisor-summary__item">
        <span class="size-advisor-summary__label">${this.translations.summaryWidth}:</span>
        ${context.referenceDisplay}
      </span>
    `;
  }

  findRecommendations(targetWidth) {
    if (this.sizeVariants.length === 0) {
      return { recommended: null, subtle: null, statement: null };
    }

    let closestIndex = 0;
    let closestDiff = Math.abs(this.sizeVariants[0].width - targetWidth);

    this.sizeVariants.forEach((variant, index) => {
      const diff = Math.abs(variant.width - targetWidth);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = index;
      }
    });

    return {
      recommended: this.sizeVariants[closestIndex],
      subtle: closestIndex > 0 ? this.sizeVariants[closestIndex - 1] : null,
      statement: closestIndex < this.sizeVariants.length - 1 ? this.sizeVariants[closestIndex + 1] : null
    };
  }

  renderResults(recommendations, context) {
    if (!this.resultsList || !this.resultsContainer) return;

    const { recommended, subtle, statement } = recommendations;
    this.recommendationMap = {};

    if (!recommended) {
      this.resultsList.innerHTML = `
        <div class="size-advisor-no-results">
          ${this.translations.noResults}
        </div>
      `;
      this.resultsContainer.classList.add('is-visible');
      return;
    }

    let html = this.renderPrimaryRecommendation(recommended, context);

    const alternatives = [
      subtle ? this.renderAlternativeRecommendation(subtle, context, 'subtle') : '',
      statement ? this.renderAlternativeRecommendation(statement, context, 'statement') : ''
    ].filter(Boolean);

    if (alternatives.length) {
      html += `
        <details class="size-advisor-alternatives">
          <summary>${this.translations.seeOtherLooks}</summary>
          <div class="size-advisor-alternatives__content">
            <p class="size-advisor-alternatives__title">${this.translations.preferDifferentLook}</p>
            <div class="size-advisor-alternatives__list">
              ${alternatives.join('')}
            </div>
          </div>
        </details>
      `;
    }

    this.resultsList.innerHTML = html;
    this.resultsContainer.classList.add('is-visible');
  }

  renderPrimaryRecommendation(variant, context) {
    const meta = this.buildRecommendationMeta(variant, context, 'recommended');
    this.recommendationMap[String(variant.id)] = meta;

    const description = this.buildRecommendationDescription(meta);
    const installment = this.formatMoney(Math.round(variant.price / 3));
    const setBadge = this.setCount > 1
      ? `<div class="size-advisor-set-badge"><span>${this.translations.setOf} ${this.setCount} — ${this.translations.sizePerPiece}</span></div>`
      : '';

    return `
      <section class="size-advisor-primary-card">
        <p class="size-advisor-primary-card__kicker">${this.translations.recommendedHeading}</p>
        <h4 class="size-advisor-primary-card__title">
          ${formatDualSize(variant.width, variant.height, this.currentUnit)}
          <span>${this.translations.balancedFit}</span>
        </h4>
        <p class="size-advisor-primary-card__description">${description}</p>
        ${setBadge}
        ${this.renderScale(meta)}
        <div class="size-advisor-primary-card__price-wrap">
          <span class="size-advisor-primary-card__price">${variant.formattedPrice}</span>
          <span class="size-advisor-primary-card__installment">${this.translations.threePaymentsOf} ${installment}</span>
        </div>
        <button type="button" class="size-advisor-primary-card__action" data-size-advisor-select="${variant.id}" ${variant.available ? '' : 'disabled'}>
          ${variant.available ? this.translations.chooseRecommended : this.translations.soldOut}
        </button>
        <p class="size-advisor-primary-card__benefits">${this.translations.shippingBenefits}</p>
      </section>
    `;
  }

  renderAlternativeRecommendation(variant, context, look) {
    const meta = this.buildRecommendationMeta(variant, context, look);
    this.recommendationMap[String(variant.id)] = meta;

    const lookLabel = look === 'subtle' ? this.translations.subtleLook : this.translations.statementLook;
    return `
      <button type="button" class="size-advisor-look-option" data-size-advisor-select="${variant.id}" ${variant.available ? '' : 'disabled'}>
        <span class="size-advisor-look-option__title">${lookLabel}: ${formatDualSize(variant.width, variant.height, this.currentUnit)}</span>
        <span class="size-advisor-look-option__meta">${meta.ratio}% ${this.translations.ofReferenceWidth.replace('{{ reference }}', `${meta.referenceDisplay} ${meta.referenceLabel}`)}</span>
      </button>
    `;
  }

  buildRecommendationMeta(variant, context, look) {
    const totalSpan = this.getArtworkSpan(variant.width);
    const ratio = Math.round((totalSpan / context.referenceWidth) * 100);
    const aspectRatio = variant.height / variant.width;

    return {
      variantId: variant.id,
      variant,
      look,
      ratio,
      aspectRatio,
      sizeKey: `${variant.width}x${variant.height}`,
      referenceDisplay: context.referenceDisplay,
      referenceLabel: context.referenceLabel,
      referenceWidth: context.referenceWidth,
      totalSpan,
      totalSpanDisplay: formatDualLength(totalSpan, this.currentUnit),
      placement: context.placement
    };
  }

  buildRecommendationDescription(meta) {
    const prefix = `${this.translations.recommendedFor} ${meta.referenceDisplay} ${meta.referenceLabel}.`;
    const template = meta.placement === 'wall' ? this.translations.wallFitExplanation : this.translations.fitExplanation;
    return `${prefix} ${template.replace('{{ percent }}', `${meta.ratio}%`)}`;
  }

  renderScale(meta) {
    const scene = this.getSceneLayout(meta);
    const artWidth = Math.max(56, Math.min(scene.referenceWidth, Math.round((meta.ratio / 100) * scene.referenceWidth)));
    const rawArtHeight = Math.round(artWidth * meta.aspectRatio);
    const artHeight = Math.max(32, Math.min(scene.maxArtHeight, rawArtHeight));
    const artX = Math.round(scene.referenceX + ((scene.referenceWidth - artWidth) / 2));
    const artY = scene.artTop + Math.max(0, Math.round((scene.maxArtHeight - artHeight) / 2));
    const placementShape = this.renderPlacementScene(meta.placement, scene);

    return `
      <div class="size-advisor-scene">
        <svg class="size-advisor-scene__svg" viewBox="0 0 260 180" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <rect class="size-advisor-scene__wall-panel" x="24" y="20" width="212" height="122" rx="10" />
          <line class="size-advisor-scene__anchor" x1="130" y1="40" x2="130" y2="${artY - 6}" />
          <rect class="size-advisor-scene__art-shadow" x="${artX + 3}" y="${artY + 4}" width="${artWidth}" height="${artHeight}" rx="6" />
          <rect class="size-advisor-scene__art-frame" x="${artX}" y="${artY}" width="${artWidth}" height="${artHeight}" rx="6" />
          <rect class="size-advisor-scene__art-surface" x="${artX + 6}" y="${artY + 6}" width="${Math.max(10, artWidth - 12)}" height="${Math.max(10, artHeight - 12)}" rx="4" />
          ${placementShape}
        </svg>
        <div class="size-advisor-scene__labels">
          <span>${this.translations.artworkSpan}: ${meta.totalSpanDisplay}</span>
          <span>${meta.ratio}% ${this.translations.ofReferenceWidth.replace('{{ reference }}', `${meta.referenceDisplay} ${meta.referenceLabel}`)}</span>
        </div>
      </div>
    `;
  }

  getSceneLayout(meta) {
    if (meta.placement === 'bed') {
      return {
        referenceX: 52,
        referenceWidth: 156,
        artTop: 34,
        maxArtHeight: 60,
        floorY: 150,
        baseY: 118
      };
    }

    if (meta.placement === 'sideboard') {
      return {
        referenceX: 58,
        referenceWidth: 144,
        artTop: 34,
        maxArtHeight: 60,
        floorY: 150,
        baseY: 120
      };
    }

    if (meta.placement === 'wall') {
      return {
        referenceX: 46,
        referenceWidth: 168,
        artTop: 34,
        maxArtHeight: 62,
        floorY: 150,
        baseY: 130
      };
    }

    return {
      referenceX: 50,
      referenceWidth: 160,
      artTop: 34,
      maxArtHeight: 60,
      floorY: 150,
      baseY: 118
    };
  }

  renderPlacementScene(placement, scene) {
    if (placement === 'bed') {
      return `
        <line class="size-advisor-scene__floor" x1="40" y1="${scene.floorY}" x2="220" y2="${scene.floorY}" />
        <path class="size-advisor-scene__furniture-fill" d="M52 ${scene.baseY}V94H96C104 94 110 100 110 108V${scene.baseY}Z" />
        <path class="size-advisor-scene__furniture-fill" d="M150 94H196V${scene.baseY}H150Z" />
        <path class="size-advisor-scene__furniture-stroke" d="M52 ${scene.baseY}V94H96C104 94 110 100 110 108V${scene.baseY}" />
        <path class="size-advisor-scene__furniture-stroke" d="M150 94H196V${scene.baseY}" />
        <line class="size-advisor-scene__furniture-stroke" x1="52" y1="${scene.baseY}" x2="196" y2="${scene.baseY}" />
        <line class="size-advisor-scene__furniture-stroke" x1="110" y1="102" x2="150" y2="102" />
        <line class="size-advisor-scene__furniture-detail" x1="76" y1="${scene.baseY}" x2="76" y2="138" />
        <line class="size-advisor-scene__furniture-detail" x1="174" y1="${scene.baseY}" x2="174" y2="138" />
      `;
    }

    if (placement === 'sideboard') {
      return `
        <line class="size-advisor-scene__floor" x1="38" y1="${scene.floorY}" x2="222" y2="${scene.floorY}" />
        <rect class="size-advisor-scene__furniture-fill" x="60" y="92" width="140" height="28" rx="4" />
        <rect class="size-advisor-scene__furniture-stroke" x="60" y="92" width="140" height="28" rx="4" />
        <line class="size-advisor-scene__furniture-detail" x1="82" y1="120" x2="82" y2="140" />
        <line class="size-advisor-scene__furniture-detail" x1="178" y1="120" x2="178" y2="140" />
        <line class="size-advisor-scene__furniture-detail" x1="130" y1="92" x2="130" y2="120" />
      `;
    }

    if (placement === 'wall') {
      return `
        <line class="size-advisor-scene__floor" x1="46" y1="${scene.floorY}" x2="214" y2="${scene.floorY}" />
        <line class="size-advisor-scene__wall-guide" x1="${scene.referenceX}" y1="118" x2="${scene.referenceX + scene.referenceWidth}" y2="118" />
        <line class="size-advisor-scene__furniture-detail" x1="130" y1="118" x2="130" y2="144" />
      `;
    }

    return `
      <line class="size-advisor-scene__floor" x1="34" y1="${scene.floorY}" x2="226" y2="${scene.floorY}" />
      <path class="size-advisor-scene__furniture-fill" d="M54 ${scene.baseY}V94H92C101 94 108 101 108 110V${scene.baseY}Z" />
      <path class="size-advisor-scene__furniture-fill" d="M152 94H206V${scene.baseY}H152C152 102 145 109 136 109H108V102H136C144 102 150 108 150 ${scene.baseY}Z" />
      <path class="size-advisor-scene__furniture-stroke" d="M54 ${scene.baseY}V94H92C101 94 108 101 108 110V${scene.baseY}" />
      <path class="size-advisor-scene__furniture-stroke" d="M108 102H136C144 102 150 108 150 ${scene.baseY}" />
      <path class="size-advisor-scene__furniture-stroke" d="M152 94H206V${scene.baseY}" />
      <line class="size-advisor-scene__furniture-stroke" x1="54" y1="${scene.baseY}" x2="206" y2="${scene.baseY}" />
      <line class="size-advisor-scene__furniture-detail" x1="82" y1="${scene.baseY}" x2="82" y2="138" />
      <line class="size-advisor-scene__furniture-detail" x1="112" y1="${scene.baseY}" x2="112" y2="136" />
      <line class="size-advisor-scene__furniture-detail" x1="142" y1="${scene.baseY}" x2="142" y2="136" />
      <line class="size-advisor-scene__furniture-detail" x1="178" y1="${scene.baseY}" x2="178" y2="138" />
    `;
  }

  getArtworkSpan(singleWidth) {
    if (this.setCount <= 1) return singleWidth;
    return (singleWidth * this.setCount) + ((this.setCount - 1) * 8);
  }

  updateWhatsAppLink(context) {
    if (!this.whatsappLink) return;

    const message = `Salam alaykum, can you help me choose a size for ${this.productTitle}? Placement: ${context.placementLabel}, width: ${Math.round(context.referenceWidth)}cm.`;
    const encodedMessage = encodeURIComponent(message);
    const cleanNumber = this.whatsappNumber.replace(/[^0-9]/g, '');
    this.whatsappLink.href = `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
  }

  selectVariant(variantId) {
    this.suppressNextSizeChange = true;

    const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
    const variantSelect = document.querySelector('select[name="id"]');

    if (variantInput) {
      variantInput.checked = true;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (variantSelect) {
      variantSelect.value = variantId;
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const selectedVariant = this.variants.find((variant) => variant.id == variantId);
    if (selectedVariant) {
      const normalizedVariantTitle = selectedVariant.title.replace(/×/g, 'x').replace(/\s*x\s*/gi, 'x').toLowerCase();

      const sizeButtons = document.querySelectorAll('[data-option-type] input[type="radio"], [data-picker-field] input[type="radio"]');
      sizeButtons.forEach((btn) => {
        const normalizedBtnValue = btn.value.replace(/×/g, 'x').replace(/\s*x\s*/gi, 'x').toLowerCase();
        if (normalizedBtnValue === normalizedVariantTitle || normalizedVariantTitle.includes(normalizedBtnValue)) {
          btn.checked = true;
          btn.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      const sizeSelects = document.querySelectorAll('select[name^="options"]');
      sizeSelects.forEach((select) => {
        Array.from(select.options).forEach((option) => {
          const normalizedOptionValue = option.value.replace(/×/g, 'x').replace(/\s*x\s*/gi, 'x').toLowerCase();
          if (normalizedOptionValue === normalizedVariantTitle || normalizedVariantTitle.includes(normalizedOptionValue)) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    const recommendationMeta = this.recommendationMap[String(variantId)];
    if (recommendationMeta) {
      this.applyRecommendationToPdp(recommendationMeta);
    }

    this.close();
    setTimeout(() => this.scrollToFrameOption(), 180);
  }

  applyRecommendationToPdp(meta) {
    this.selectedRecommendation = meta;
    this.setTriggerNudgeState(false);

    if (!this.triggerContext || !this.triggerWrap) return;

    this.triggerWrap.classList.add('has-context');
    this.triggerContext.hidden = false;
    this.triggerContext.textContent = this.translations.pdpRecommendation.replace('{{ reference }}', `${meta.referenceDisplay} ${meta.referenceLabel}`);
  }

  scrollToFrameOption() {
    const frameOption = Array.from(document.querySelectorAll('.m-product-option')).find((container) => {
      const label = container.querySelector('.m-product-option--label');
      const text = label ? label.textContent.toLowerCase() : '';
      return /frame|lijst|kader|framed/.test(text);
    });

    if (!frameOption) return;
    frameOption.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  handleProductOptionChange() {
    const currentSizeKey = this.getCurrentSelectedSizeKey();
    if (!currentSizeKey || currentSizeKey === this.lastSelectedSizeKey) return;

    this.lastSelectedSizeKey = currentSizeKey;

    if (this.suppressNextSizeChange) {
      this.suppressNextSizeChange = false;
      return;
    }

    this.variantSwitchCount += 1;
    if (this.variantSwitchCount >= this.autoPromptThreshold && !this.autoPromptShown) {
      this.autoPromptShown = true;
      this.setTriggerNudgeState(true);
    }
  }

  setTriggerNudgeState(isNudged) {
    if (!this.triggerWrap || !this.triggerTitle || !this.triggerSubtitle) return;

    this.triggerWrap.classList.toggle('is-nudged', isNudged);

    if (isNudged) {
      this.triggerTitle.textContent = this.translations.autoPromptTitle || this.triggerDefaultTitle;
      this.triggerSubtitle.textContent = this.translations.autoPromptBody || this.triggerDefaultSubtitle;
      return;
    }

    this.triggerTitle.textContent = this.triggerDefaultTitle;
    this.triggerSubtitle.textContent = this.triggerDefaultSubtitle;
  }

  getCurrentSelectedSizeKey() {
    if (!this.optionContainer) return '';
    const selectedSize = getSelectedSizeFromContainer(this.optionContainer);
    return selectedSize ? `${selectedSize.width}x${selectedSize.height}` : '';
  }

  isFurniturePlacement(placement) {
    return placement === 'sofa' || placement === 'bed' || placement === 'sideboard';
  }

  getPlacementLabel(placement) {
    const button = Array.from(this.placementBtns).find((btn) => btn.dataset.sizeAdvisorPlacement === placement);
    return button ? button.textContent.trim() : placement;
  }

  getReferenceLabel(placement) {
    if (placement === 'bed') return this.translations.referenceBed;
    if (placement === 'sideboard') return this.translations.referenceSideboard;
    if (placement === 'wall') return this.translations.referenceWall;
    return this.translations.referenceSofa;
  }
}

// Initialize all Size Advisor instances when DOM is ready
function initSizeAdvisor() {
  const containers = document.querySelectorAll('[data-size-advisor]');
  containers.forEach(container => {
    new SizeAdvisor(container);
  });
  
  // Add unit switcher and enhance size variant labels
  initSizeUnitSwitcher();
  initMobileHybridSelectors();

  // Product-specific layout enhancements
  initProductLayoutEnhancements();
}

/**
 * Initialize unit switcher for size variants
 */
function initSizeUnitSwitcher() {
  const defaultUnit = getDefaultSizeUnit();

  // Find size option containers
  const sizeOptions = document.querySelectorAll('.m-product-option');
  
  sizeOptions.forEach(optionContainer => {
    // Check if this is a size option by looking for size patterns in values
    const labels = optionContainer.querySelectorAll('.m-product-option--node__label, option');
    let hasSizeValues = false;
    
    labels.forEach(label => {
      const text = label.textContent.trim();
      if (parseSizePair(text)) {
        hasSizeValues = true;
      }
    });
    
    if (!hasSizeValues) {
      cleanupNonSizeOptionContainer(optionContainer);
      return;
    }

    optionContainer.classList.add('size-option-grid');

    const existingSwitcher = optionContainer.querySelector('.size-unit-switcher');
    if (existingSwitcher) {
      const activeBtn = existingSwitcher.querySelector('.size-unit-btn.is-active');
      const activeUnit = activeBtn?.dataset.sizeUnit || defaultUnit;
      storeOriginalSizes(optionContainer);
      convertSizeLabels(optionContainer, activeUnit);
      return;
    }
    
    // Find the label container
    const labelContainer = optionContainer.querySelector('.m-product-option--label');
    if (!labelContainer) return;
    
    // Create unit switcher
    const switcher = document.createElement('div');
    switcher.className = 'size-unit-switcher';
    switcher.innerHTML = `
      <button type="button" class="size-unit-btn${defaultUnit === 'cm' ? ' is-active' : ''}" data-size-unit="cm">cm</button>
      <button type="button" class="size-unit-btn${defaultUnit === 'inch' ? ' is-active' : ''}" data-size-unit="inch">inch</button>
    `;
    
    // Insert after the label
    labelContainer.appendChild(switcher);
    
    // Add inline meaning hint after the selected size value
    const selectedLabel = labelContainer.querySelector('.option-label--selected');
    if (selectedLabel && !labelContainer.querySelector('.size-format-hint')) {
      const hint = document.createElement('span');
      hint.className = 'size-format-hint';
      selectedLabel.insertAdjacentElement('afterend', hint);
    }
    
    // Store original values on the whole option container (including label)
    storeOriginalSizes(optionContainer);
    
    // Handle unit switching
    switcher.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size-unit]');
      if (!btn) return;
      
      const unit = btn.dataset.sizeUnit;
      
      // Update active state
      switcher.querySelectorAll('.size-unit-btn').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      
      // Convert sizes (pass the whole option container)
      convertSizeLabels(optionContainer, unit);
    });
    
    // Initial enhancement with market/template default unit
    convertSizeLabels(optionContainer, defaultUnit);
  });
}

function getOriginalSizeFromElement(el) {
  if (!el) return null;

  const source = el.closest('[data-original-width][data-original-height]') || el;
  const width = parseInt(source.dataset.originalWidth || '', 10);
  const height = parseInt(source.dataset.originalHeight || '', 10);
  if (!Number.isNaN(width) && !Number.isNaN(height)) {
    return { width, height };
  }

  return parseSizePair(el.textContent || '');
}

function collectUniqueSizes(container) {
  const elements = container.querySelectorAll('.m-product-option--node__label, .m-custom-select__option, option, .option-label--selected, .m-custom-select__value');
  const unique = new Map();

  elements.forEach((el) => {
    const size = getOriginalSizeFromElement(el);
    if (!size) return;
    const key = `${size.width}x${size.height}`;
    if (!unique.has(key)) {
      unique.set(key, { key, width: size.width, height: size.height });
    }
  });

  return Array.from(unique.values()).sort((a, b) => a.width - b.width);
}

function isSizeOptionContainer(container) {
  if (!container) return false;
  if (container.classList.contains('size-option-grid')) return true;
  if (container.querySelector('.size-unit-switcher')) return true;

  const elements = container.querySelectorAll('.m-product-option--node__label, .m-custom-select__option, option, .option-label--selected, .m-custom-select__value');
  for (const el of elements) {
    const source = el.closest('[data-original-width][data-original-height]') || el;
    if (source.dataset.originalWidth && source.dataset.originalHeight) {
      return true;
    }

    if (parseSizePair(el.textContent || '')) {
      return true;
    }
  }

  return false;
}

function cleanupNonSizeOptionContainer(container) {
  if (!container) return;

  const hybridRoot = container.querySelector('[data-size-hybrid-mobile]');
  if (hybridRoot) {
    hybridRoot.remove();
  }

  const clarityStack = container.querySelector('.size-clarity-stack');
  if (clarityStack) {
    clarityStack.remove();
  }

  container.classList.remove('size-hybrid-enabled');
}

function pickRecommendedSize(sizes) {
  if (!sizes.length) return null;

  if (sizes.length === 1) {
    return sizes[0];
  }

  // Always highlight the second-largest size as the primary social-proof choice.
  return sizes[sizes.length - 2];
}

function applyRecommendedSizeBadge(container) {
  const swatchTargets = Array.from(container.querySelectorAll('.m-product-option--node__label'));
  const dropdownTargets = Array.from(container.querySelectorAll('.m-custom-select__option > span:first-child'));
  const badgeTargets = swatchTargets.length ? swatchTargets : dropdownTargets;

  badgeTargets.forEach((target) => {
    target.classList.remove('size-option-is-recommended');
    const badge = target.querySelector('.size-option-recommended-badge');
    if (badge) badge.remove();
  });
}

function getSelectedSizeFromContainer(container) {
  syncSelectedLabelOriginalSize(container);

  const selectedLabel = container.querySelector('.option-label--selected');
  let size = getOriginalSizeFromElement(selectedLabel);

  if (!size) {
    const selectedOption = container.querySelector('.m-custom-select__option[aria-selected="true"], .m-custom-select__option.is-selected');
    size = getOriginalSizeFromElement(selectedOption);
  }

  if (!size) {
    const checkedInput = container.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      const forLabel = checkedInput.id ? container.querySelector(`label[for="${checkedInput.id}"]`) : null;
      size = getOriginalSizeFromElement(forLabel || checkedInput.closest('label'));
    }
  }

  if (!size) {
    size = getOriginalSizeFromElement(container.querySelector('.m-custom-select__value'));
  }

  if (size && selectedLabel) {
    selectedLabel.dataset.originalWidth = String(size.width);
    selectedLabel.dataset.originalHeight = String(size.height);
  }

  return size;
}

function syncSelectedLabelOriginalSize(container) {
  const selectedLabel = container.querySelector('.option-label--selected');
  if (!selectedLabel) return;

  let sourceEl = null;
  const checkedInput = container.querySelector('input[type="radio"]:checked');
  if (checkedInput && checkedInput.id) {
    sourceEl = container.querySelector(`label[for="${checkedInput.id}"]`);
  }

  if (!sourceEl) {
    sourceEl = container.querySelector('.m-custom-select__option[aria-selected="true"], .m-custom-select__option.is-selected');
  }

  if (!sourceEl) return;

  const source = sourceEl.closest('[data-original-width][data-original-height]') || sourceEl;
  if (source.dataset.originalWidth && source.dataset.originalHeight) {
    selectedLabel.dataset.originalWidth = source.dataset.originalWidth;
    selectedLabel.dataset.originalHeight = source.dataset.originalHeight;
  }
}

function updateSizeClarityCopy(container, unit) {
  const translations = getSizeAdvisorChoiceCopy();
  const sizes = collectUniqueSizes(container);
  const setCount = getProductSetCount();
  let clarityStack = container.querySelector('.size-clarity-stack');
  const adviceHost = container.querySelector('[data-size-advice-slot]') || container;

  const selectedSize = getSelectedSizeFromContainer(container);
  if (!selectedSize) {
    if (clarityStack) {
      clarityStack.remove();
    }
    return;
  }

  if (!clarityStack) {
    clarityStack = document.createElement('details');
    clarityStack.className = 'size-clarity-stack';
    const summary = document.createElement('summary');
    summary.className = 'size-clarity-stack__summary';
    clarityStack.appendChild(summary);
    const body = document.createElement('div');
    body.className = 'size-clarity-stack__body';
    clarityStack.appendChild(body);
    adviceHost.appendChild(clarityStack);
  } else if (clarityStack.parentNode !== adviceHost) {
    adviceHost.appendChild(clarityStack);
  }

  let clarityBody = clarityStack.querySelector('.size-clarity-stack__body');
  if (!clarityBody) {
    clarityBody = document.createElement('div');
    clarityBody.className = 'size-clarity-stack__body';
    clarityStack.appendChild(clarityBody);
  }

  let selectedNote = clarityBody.querySelector('.size-selected-microcopy');
  if (!selectedNote) {
    selectedNote = document.createElement('p');
    selectedNote.className = 'size-selected-microcopy';
    clarityBody.appendChild(selectedNote);
  }

  let confidenceNote = clarityBody.querySelector('.size-confidence-copy');
  if (!confidenceNote) {
    confidenceNote = document.createElement('p');
    confidenceNote.className = 'size-confidence-copy';
    clarityBody.appendChild(confidenceNote);
  }

  const descriptorKey = getSizeDescriptorKey(selectedSize, sizes);
  const descriptorLabel = getSizeDescriptorLabel(descriptorKey, translations);
  const descriptorHelp = getSizeDescriptorHelp(descriptorKey, translations);
  const selectedSizeLabel = formatDualSize(selectedSize.width, selectedSize.height, unit);

  const summaryEl = clarityStack.querySelector('.size-clarity-stack__summary');
  if (summaryEl) {
    const titleTemplate = translations.sizeAdviceRecommendTitle || 'Why we recommend {{ size }}';
    summaryEl.textContent = titleTemplate.replace('{{ size }}', selectedSizeLabel);
  }
  const usageText = descriptorHelp || `a ${descriptorLabel || translations.sizeUseThreeSeater} setup.`;
  const whyPrefix = translations.sizeAdviceWhyPrefix || 'Why this works:';
  selectedNote.innerHTML = `<strong>${whyPrefix}</strong> ${selectedSizeLabel} works best for ${usageText}`;

  const selectedIndex = sizes.findIndex((entry) => entry.width === selectedSize.width && entry.height === selectedSize.height);
  const safeSelectedIndex = selectedIndex === -1 ? Math.max(0, sizes.length - 2) : selectedIndex;
  const fitKey = getFitKeyByCatalogPosition(safeSelectedIndex, Math.max(sizes.length, 1));
  const fitLabel = getFitLabel(fitKey, translations).toLowerCase();
  const descriptorWhy = getDescriptorAdviceWhy(descriptorKey, translations);
  const fallbackPersuasion = (translations.sizeAdvicePersuasion || 'This size gives a {{ fit }} look and keeps the artwork in proportion with your space.')
    .replace('{{ fit }}', fitLabel);
  const whyBody = descriptorWhy || fallbackPersuasion;
  const proofText = getSizeAdviceProof(fitKey, translations);
  confidenceNote.textContent = `${whyBody}${proofText ? ` ${proofText}` : ''}`;

  let setNote = clarityBody.querySelector('.size-set-clarity');

  if (setCount > 1) {
    if (!setNote) {
      setNote = document.createElement('p');
      setNote.className = 'size-set-clarity';
      clarityBody.appendChild(setNote);
    }

    const totalSpanCm = (selectedSize.width * setCount) + ((setCount - 1) * 8);
    setNote.textContent = `Set of ${setCount} - size shown per piece. Total wall span approx ${formatDualLength(totalSpanCm, unit)}.`;
    return;
  }

  if (setNote) {
    setNote.remove();
  }
}

let __sizeVariantCatalogCache = null;
let __sizeAdvisorRawDataCache = null;
let __activeSizeHybridSheet = null;
let __sizeHybridSheetKeyHandler = null;

function formatMoneyWithTemplate(cents, moneyFormat) {
  const normalized = typeof cents === 'string' ? cents.replace('.', '') : cents;
  const value = (parseInt(normalized, 10) / 100).toFixed(2);

  return (moneyFormat || '€{{amount}}')
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', Math.round(value))
    .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
}

/**
 * Size tiles show one price per size, but a size has one variant per frame style.
 * The tile price has to follow the frame the shopper actually selected, otherwise a
 * "No Frame" selection keeps showing the framed price.
 */
function buildStyleKeyFromValues(values, sizeOptionIndex) {
  return values
    .filter((value, index) => (sizeOptionIndex >= 0 ? index !== sizeOptionIndex : !parseSizePair(String(value))))
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean)
    .join(' / ');
}

function getSizeOptionIndex(variants) {
  const hits = [];

  variants.forEach((variant) => {
    const options = Array.isArray(variant.options) ? variant.options : [];
    options.forEach((value, index) => {
      if (parseSizePair(String(value))) {
        hits[index] = (hits[index] || 0) + 1;
      }
    });
  });

  let bestIndex = -1;
  let bestCount = 0;
  hits.forEach((count, index) => {
    if (count > bestCount) {
      bestCount = count;
      bestIndex = index;
    }
  });

  return bestIndex;
}

function getVariantStyleKey(variant, sizeOptionIndex) {
  const options = Array.isArray(variant.options) && variant.options.length ? variant.options : null;
  if (options) return buildStyleKeyFromValues(options, sizeOptionIndex);

  return buildStyleKeyFromValues(String(variant.title || '').split(' / '), -1);
}

function getSizeAdvisorPickerScope() {
  const dataEl = document.querySelector('[data-size-advisor-data]');
  const scoped = dataEl && dataEl.closest('[data-variant-picker]');
  if (scoped) return scoped;

  const mainProduct = document.querySelector('.m-main-product--wrapper');
  const mainPicker = mainProduct && mainProduct.querySelector('[data-variant-picker]');

  return mainPicker || document.querySelector('[data-variant-picker]') || document;
}

function getSelectedOptionValues() {
  const fields = Array.from(getSizeAdvisorPickerScope().querySelectorAll('[data-picker-field]'));

  return fields.map((field) => {
    const checked = field.querySelector('input[type="radio"]:checked');
    if (checked) return String(checked.value).trim();

    const select = field.querySelector('select');
    if (select && select.value) return String(select.value).trim();

    return String(field.getAttribute('data-selected-value') || '').trim();
  });
}

function getSelectedVariantId() {
  const scopes = [document.querySelector('form[data-type="add-to-cart-form"]'), document].filter(Boolean);

  for (const scope of scopes) {
    const checked = scope.querySelector('input[name="id"]:checked');
    if (checked && checked.value) return checked.value;

    const select = scope.querySelector('select[name="id"]');
    if (select && select.value) return select.value;

    const hidden = scope.querySelector('input[name="id"]');
    if (hidden && hidden.value) return hidden.value;
  }

  return null;
}

/**
 * The option pickers reflect the shopper's click immediately, so they win over the
 * hidden variant input, which is only updated once the theme resolves the variant.
 */
function getSelectedStyleKey(variants) {
  const list = Array.isArray(variants) ? variants : [];
  const sizeOptionIndex = getSizeOptionIndex(list);
  const optionCount = list.length && Array.isArray(list[0].options) ? list[0].options.length : 0;

  const pickerValues = getSelectedOptionValues();
  if (pickerValues.some(Boolean)) {
    const key = buildStyleKeyFromValues(pickerValues, pickerValues.length === optionCount ? sizeOptionIndex : -1);
    if (key) return key;
  }

  const selectedId = getSelectedVariantId();
  if (selectedId) {
    const selectedVariant = list.find((variant) => String(variant.id) === String(selectedId));
    if (selectedVariant) {
      const key = getVariantStyleKey(selectedVariant, sizeOptionIndex);
      if (key) return key;
    }
  }

  return '';
}

function isBetterSizeCandidate(candidate, existing) {
  if (candidate.matchesStyle !== existing.matchesStyle) return candidate.matchesStyle;
  if (candidate.available !== existing.available) return candidate.available;

  return false;
}

function buildSizeVariantCatalog(variants, styleKey) {
  const list = Array.isArray(variants) ? variants : [];
  const sizeOptionIndex = getSizeOptionIndex(list);
  const normalizedStyleKey = String(styleKey || '').trim().toLowerCase();
  const seen = new Map();

  list.forEach((variant) => {
    const options = Array.isArray(variant.options) ? variant.options : [];
    const sizeSource = sizeOptionIndex >= 0 && options[sizeOptionIndex] !== undefined
      ? String(options[sizeOptionIndex])
      : String(variant.title || '');

    const size = parseSizePair(sizeSource);
    if (!size) return;

    const key = `${size.width}x${size.height}`;
    const candidate = {
      id: variant.id,
      key,
      width: size.width,
      height: size.height,
      price: variant.price,
      available: !!variant.available,
      matchesStyle: normalizedStyleKey !== '' && getVariantStyleKey(variant, sizeOptionIndex) === normalizedStyleKey
    };

    const existing = seen.get(key);
    if (!existing || isBetterSizeCandidate(candidate, existing)) {
      seen.set(key, candidate);
    }
  });

  return Array.from(seen.values()).sort((a, b) => a.width - b.width);
}

function getSizeAdvisorRawData() {
  if (__sizeAdvisorRawDataCache) return __sizeAdvisorRawDataCache;

  const fallback = { variants: [], moneyFormat: '€{{amount}}' };
  const dataEl = document.querySelector('[data-size-advisor-data]');

  if (!dataEl) {
    __sizeAdvisorRawDataCache = fallback;
    return __sizeAdvisorRawDataCache;
  }

  try {
    const data = JSON.parse(dataEl.textContent || '{}');
    __sizeAdvisorRawDataCache = {
      variants: Array.isArray(data.variants) ? data.variants : [],
      moneyFormat: data.moneyFormat || fallback.moneyFormat
    };
  } catch (error) {
    __sizeAdvisorRawDataCache = fallback;
  }

  return __sizeAdvisorRawDataCache;
}

function getSizeVariantCatalogData() {
  const { variants, moneyFormat } = getSizeAdvisorRawData();
  const styleKey = getSelectedStyleKey(variants);

  if (__sizeVariantCatalogCache && __sizeVariantCatalogCache.styleKey === styleKey) {
    return __sizeVariantCatalogCache;
  }

  const catalog = buildSizeVariantCatalog(variants, styleKey).map((entry) => ({
    ...entry,
    formattedPrice: formatMoneyWithTemplate(entry.price, moneyFormat)
  }));

  __sizeVariantCatalogCache = { styleKey, catalog, moneyFormat };
  return __sizeVariantCatalogCache;
}

function getCurrentSizeUnit(container) {
  const activeBtn = container.querySelector('.size-unit-switcher .size-unit-btn.is-active');
  return activeBtn?.dataset.sizeUnit || getDefaultSizeUnit();
}

function getSizeKeyFromSize(size) {
  if (!size) return '';
  return `${size.width}x${size.height}`;
}

function getRecommendedSizeSubset(catalog, selectedKey) {
  if (!catalog.length) return [];
  if (catalog.length <= 3) return [...catalog];

  let centerIndex = catalog.findIndex((entry) => entry.key === selectedKey);
  if (centerIndex === -1) {
    centerIndex = Math.max(0, catalog.length - 2);
  }

  const chosen = new Set([centerIndex]);
  let offset = 1;

  while (chosen.size < 3) {
    const leftIndex = centerIndex - offset;
    const rightIndex = centerIndex + offset;

    if (leftIndex >= 0) {
      chosen.add(leftIndex);
    }

    if (chosen.size < 3 && rightIndex < catalog.length) {
      chosen.add(rightIndex);
    }

    offset += 1;
    if (leftIndex < 0 && rightIndex >= catalog.length) break;
  }

  return Array.from(chosen)
    .sort((a, b) => a - b)
    .map((index) => catalog[index]);
}

function getFitKeyForRecommendedPosition(index, total) {
  if (total <= 1) return 'balanced';
  if (total === 2) {
    return index === 0 ? 'balanced' : 'statement';
  }

  if (index === 0) return 'subtle';
  if (index === total - 1) return 'statement';
  return 'balanced';
}

function getFitKeyByCatalogPosition(index, total) {
  if (total <= 1) return 'balanced';

  const ratio = index / (total - 1);
  if (ratio < 0.34) return 'subtle';
  if (ratio > 0.66) return 'statement';
  return 'balanced';
}

function getFitLabel(fitKey, translations) {
  if (fitKey === 'subtle') return translations.fitSubtle || `${translations.subtleLook} fit`;
  if (fitKey === 'statement') return translations.fitStatement || `${translations.statementLook} fit`;
  return translations.fitBalanced || translations.balancedFit;
}

function getHybridGroupKey(descriptorKey) {
  if (descriptorKey === 'hallway' || descriptorKey === 'sideboard') return 'small';
  if (descriptorKey === 'bedOrWideSofa' || descriptorKey === 'statementWall') return 'large';
  return 'balanced';
}

function ensureHybridRoot(container) {
  let root = container.querySelector('[data-size-hybrid-mobile]');
  if (root) return root;

  root = document.createElement('div');
  root.className = 'size-hybrid-mobile';
  root.setAttribute('data-size-hybrid-mobile', '');
  root.dataset.hybridDragging = '0';
  root.dataset.hybridDragUntil = '0';
  container.appendChild(root);
  container.classList.add('size-hybrid-enabled');

  root.addEventListener('click', (event) => {
    const selectBtn = event.target.closest('[data-hybrid-size-select]');
    if (selectBtn) {
      const dragUntil = parseInt(root.dataset.hybridDragUntil || '0', 10);
      if (root.dataset.hybridDragging === '1' || (!Number.isNaN(dragUntil) && dragUntil > Date.now())) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      const sizeKey = selectBtn.dataset.hybridSizeSelect;
      if (!sizeKey) return;

      selectSizeInOptionContainer(container, sizeKey);
      setTimeout(() => {
        refreshHybridSelectorAndAdvice(container, getCurrentSizeUnit(container));
      }, 120);
      return;
    }

    const viewAllBtn = event.target.closest('[data-hybrid-open-sheet]');
    if (!viewAllBtn) return;

    event.preventDefault();
    openHybridSizeSheet(container, getCurrentSizeUnit(container));
  });

  return root;
}

function getCatalogEntryByKey(catalog, key) {
  return catalog.find((entry) => entry.key === key) || null;
}

function easeInOutCubic(value) {
  return value < 0.5
    ? 4 * value * value * value
    : 1 - Math.pow(-2 * value + 2, 3) / 2;
}

function animateHorizontalScroll(container, targetLeft, durationMs = 300) {
  const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
  const clampedTarget = Math.min(maxScroll, Math.max(0, targetLeft));
  const startLeft = container.scrollLeft;
  const distance = clampedTarget - startLeft;

  if (Math.abs(distance) < 1) return;

  const startTime = performance.now();

  const step = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(1, elapsed / durationMs);
    const eased = easeInOutCubic(progress);

    container.scrollLeft = startLeft + (distance * eased);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };

  requestAnimationFrame(step);
}

function initHybridCarousel(root, selectedKey) {
  const viewport = root.querySelector('[data-hybrid-carousel-viewport]');
  const track = root.querySelector('[data-hybrid-carousel-track]');
  const prevBtn = root.querySelector('[data-hybrid-nav="prev"]');
  const nextBtn = root.querySelector('[data-hybrid-nav="next"]');

  if (!viewport || !track || !prevBtn || !nextBtn) return;

  const applyCardWidth = () => {
    const trackStyles = window.getComputedStyle(track);
    const gapValue = parseFloat(trackStyles.columnGap || trackStyles.gap || '8');
    const safeGap = Number.isNaN(gapValue) ? 8 : gapValue;
    const horizontalGapTotal = safeGap * 2;
    const viewportWidth = viewport.clientWidth;
    if (!viewportWidth) return;

    const computedWidth = Math.max(88, Math.floor((viewportWidth - horizontalGapTotal) / 3));
    root.style.setProperty('--size-hybrid-card-width', `${computedWidth}px`);
  };

  applyCardWidth();

  const getStepSize = () => {
    const firstCard = track.querySelector('.size-hybrid-mobile__option');
    if (!firstCard) return viewport.clientWidth;

    const styles = window.getComputedStyle(track);
    const gapValue = parseFloat(styles.columnGap || styles.gap || '8');
    return firstCard.getBoundingClientRect().width + (Number.isNaN(gapValue) ? 8 : gapValue);
  };

  const getMaxScrollLeft = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);

  const getCenteredLeftForCard = (card) => {
    if (!card) return viewport.scrollLeft;

    const cardWidth = card.getBoundingClientRect().width;
    const rawTarget = card.offsetLeft - ((viewport.clientWidth - cardWidth) / 2);
    const maxLeft = getMaxScrollLeft();

    return Math.max(0, Math.min(maxLeft, rawTarget));
  };

  const centerSelectedCard = (activeKey, animated = false) => {
    const cards = Array.from(track.querySelectorAll('[data-hybrid-size-select]'));
    if (!cards.length) return;

    const selectedCard = cards.find((card) => card.dataset.hybridSizeSelect === activeKey) || cards[0];
    const centeredLeft = getCenteredLeftForCard(selectedCard);

    if (animated) {
      animateHorizontalScroll(viewport, centeredLeft, 300);
    } else {
      viewport.scrollLeft = centeredLeft;
    }
  };

  const updateNavState = () => {
    const maxLeft = getMaxScrollLeft();
    prevBtn.disabled = viewport.scrollLeft <= 4;
    nextBtn.disabled = viewport.scrollLeft >= (maxLeft - 4);
  };

  const shiftCarousel = (direction) => {
    const nextTarget = viewport.scrollLeft + (getStepSize() * direction);
    animateHorizontalScroll(viewport, nextTarget, 300);
  };

  let touchStartX = 0;
  let touchStartY = 0;
  let isTouchTracking = false;

  const handleTouchEnd = () => {
    isTouchTracking = false;
    root.dataset.hybridDragging = '0';
  };

  viewport.addEventListener('touchstart', (event) => {
    if (!event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
    isTouchTracking = true;
    root.dataset.hybridDragging = '0';
  }, { passive: true });

  viewport.addEventListener('touchmove', (event) => {
    if (!isTouchTracking || !event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartX);
    const deltaY = Math.abs(touch.clientY - touchStartY);

    if (deltaX > 8 && deltaX > deltaY) {
      root.dataset.hybridDragging = '1';
      root.dataset.hybridDragUntil = String(Date.now() + 220);
    }
  }, { passive: true });

  viewport.addEventListener('touchend', handleTouchEnd, { passive: true });
  viewport.addEventListener('touchcancel', handleTouchEnd, { passive: true });

  prevBtn.addEventListener('click', (event) => {
    event.preventDefault();
    shiftCarousel(-1);
  });

  nextBtn.addEventListener('click', (event) => {
    event.preventDefault();
    shiftCarousel(1);
  });

  viewport.addEventListener('scroll', () => {
    requestAnimationFrame(updateNavState);
  }, { passive: true });

  requestAnimationFrame(() => {
    applyCardWidth();

    centerSelectedCard(selectedKey, false);

    updateNavState();
  });
}

function refreshHybridSelectorAndAdvice(container, unit) {
  const wasOpen = !!container.querySelector('.size-clarity-stack[open]');

  renderHybridSelectorForContainer(container, unit);
  updateSizeClarityCopy(container, unit);

  if (wasOpen) {
    const stack = container.querySelector('.size-clarity-stack');
    if (stack) stack.setAttribute('open', '');
  }
}

function renderHybridSelectorForContainer(container, unit) {
  const { catalog } = getSizeVariantCatalogData();
  if (!catalog.length) return;

  const translations = getSizeAdvisorChoiceCopy();
  const root = ensureHybridRoot(container);
  const selectedSize = getSelectedSizeFromContainer(container);
  const selectedKey = getSizeKeyFromSize(selectedSize) || catalog[Math.max(0, catalog.length - 2)].key;
  const selectedEntry = getCatalogEntryByKey(catalog, selectedKey) || catalog[0];
  const selectedBadgeText = ((translations.hybridSelectedLabel || 'Selected').replace(':', '').trim() || 'Selected');

  const cardsHtml = catalog.map((entry) => {
    const descriptorKey = getSizeDescriptorKey(entry, catalog);
    const descriptorLabel = getSizeDescriptorLabel(descriptorKey, translations);
    const isSelected = entry.key === selectedEntry.key;

    return `
      <button
        type="button"
        class="size-hybrid-mobile__option${isSelected ? ' is-selected' : ''}"
        data-hybrid-size-select="${entry.key}"
        ${entry.available ? '' : 'disabled'}
      >
        ${isSelected ? `<span class="size-hybrid-mobile__selected-badge" role="img" aria-label="${String(selectedBadgeText).replace(/"/g, '&quot;')}">
          <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M3.2 8.2L6.6 11.4L12.8 5.4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>` : ''}
        <span class="size-hybrid-mobile__option-top">
          <span class="size-hybrid-mobile__size">${formatDualSize(entry.width, entry.height, unit)}</span>
          <span class="size-hybrid-mobile__price">${entry.formattedPrice}</span>
        </span>
        ${descriptorLabel ? `<span class="size-hybrid-mobile__name">${descriptorLabel}</span>` : ''}
      </button>
    `;
  }).join('');

  root.innerHTML = `
    <div class="size-hybrid-mobile__carousel" data-hybrid-carousel>
      <div class="size-hybrid-mobile__viewport" data-hybrid-carousel-viewport>
        <div class="size-hybrid-mobile__track" data-hybrid-carousel-track>${cardsHtml}</div>
      </div>
      <button type="button" class="size-hybrid-mobile__nav size-hybrid-mobile__nav--prev" data-hybrid-nav="prev" aria-label="Show previous sizes">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M15 6L9 12L15 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button type="button" class="size-hybrid-mobile__nav size-hybrid-mobile__nav--next" data-hybrid-nav="next" aria-label="Show next sizes">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M9 6L15 12L9 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    <button type="button" class="size-hybrid-mobile__view-all" data-hybrid-open-sheet>${translations.hybridViewAllSizes}</button>
    <div class="size-hybrid-mobile__advice-slot" data-size-advice-slot></div>
  `;

  initHybridCarousel(root, selectedEntry.key);
}

function selectSizeInOptionContainer(container, sizeKey) {
  const [width, height] = sizeKey.split('x').map((value) => parseInt(value, 10));
  if (Number.isNaN(width) || Number.isNaN(height)) return;

  const radioInputs = Array.from(container.querySelectorAll('input[type="radio"]'));
  for (const input of radioInputs) {
    const label = input.id ? container.querySelector(`label[for="${input.id}"]`) : input.closest('label');
    const size = getOriginalSizeFromElement(label || input);
    if (!size || size.width !== width || size.height !== height) continue;

    if (label) {
      label.click();
    } else {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncCartSizeUnitProperty(getCurrentSizeUnit(container));
    return;
  }

  const customOptions = Array.from(container.querySelectorAll('.m-custom-select__option'));
  for (const option of customOptions) {
    const size = getOriginalSizeFromElement(option);
    if (!size || size.width !== width || size.height !== height) continue;
    option.click();
    syncCartSizeUnitProperty(getCurrentSizeUnit(container));
    return;
  }
}

function closeHybridSizeSheet() {
  if (!__activeSizeHybridSheet) return;

  const sheet = __activeSizeHybridSheet;
  sheet.classList.remove('is-open');
  document.body.style.overflow = '';

  if (__sizeHybridSheetKeyHandler) {
    document.removeEventListener('keydown', __sizeHybridSheetKeyHandler);
    __sizeHybridSheetKeyHandler = null;
  }

  setTimeout(() => {
    if (sheet.parentNode) {
      sheet.parentNode.removeChild(sheet);
    }
  }, 180);

  __activeSizeHybridSheet = null;
}

function openHybridSizeSheet(container, unit) {
  closeHybridSizeSheet();

  const { catalog } = getSizeVariantCatalogData();
  if (!catalog.length) return;

  const translations = getSizeAdvisorChoiceCopy();
  const selectedSize = getSelectedSizeFromContainer(container);
  const selectedKey = getSizeKeyFromSize(selectedSize) || catalog[0].key;

  const itemsByKey = new Map();
  const groupedItems = {
    small: [],
    balanced: [],
    large: []
  };

  const items = catalog.map((entry) => {
    const descriptorKey = getSizeDescriptorKey(entry, catalog);
    const descriptorLabel = getSizeDescriptorLabel(descriptorKey, translations);
    const groupKey = getHybridGroupKey(descriptorKey);

    const item = {
      ...entry,
      descriptorLabel,
      groupKey
    };

    if (groupedItems[groupKey]) {
      groupedItems[groupKey].push(item);
    } else {
      groupedItems.balanced.push(item);
    }

    return item;
  });
  items.forEach((entry) => {
    itemsByKey.set(entry.key, entry);
  });

  const renderSheetItem = (entry) => {
    return `
    <button
      type="button"
      class="size-hybrid-sheet__item"
      data-size-hybrid-sheet-item="${entry.key}"
    >
      <span class="size-hybrid-sheet__item-main">
        <span class="size-hybrid-sheet__item-size">${formatDualSize(entry.width, entry.height, unit)}</span>
        <span class="size-hybrid-sheet__item-right">
          <span class="size-hybrid-sheet__item-price">${entry.formattedPrice}</span>
        </span>
      </span>
      ${entry.descriptorLabel ? `<span class="size-hybrid-sheet__item-name">${entry.descriptorLabel}</span>` : ''}
      <span class="size-hybrid-sheet__item-selected" aria-hidden="true">
        <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" focusable="false">
          <path d="M3.2 8.2L6.6 11.4L12.8 5.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>${translations.hybridSelectedTag || 'SELECTED'}</span>
      </span>
    </button>
  `;
  };

  const groupDefinitions = [
    { key: 'small', title: translations.hybridGroupSmall },
    { key: 'balanced', title: translations.hybridGroupBalanced },
    { key: 'large', title: translations.hybridGroupLarge }
  ];

  const groupsHtml = groupDefinitions
    .filter((group) => groupedItems[group.key].length)
    .map((group) => {
      const itemsHtml = groupedItems[group.key].map((entry) => renderSheetItem(entry)).join('');
      return `
        <section class="size-hybrid-sheet__group">
          <h4 class="size-hybrid-sheet__group-title">${group.title}</h4>
          <div class="size-hybrid-sheet__group-list">${itemsHtml}</div>
        </section>
      `;
    })
    .join('');

  const sheet = document.createElement('div');
  sheet.className = 'size-hybrid-sheet';
  sheet.innerHTML = `
    <button type="button" class="size-hybrid-sheet__backdrop" data-size-hybrid-sheet-close aria-label="Close"></button>
    <div class="size-hybrid-sheet__panel" role="dialog" aria-modal="true" aria-label="${translations.hybridAllSizesTitle}">
      <div class="size-hybrid-sheet__header">
        <h3 class="size-hybrid-sheet__title">${translations.hybridAllSizesTitle}</h3>
        <button type="button" class="size-hybrid-sheet__close" data-size-hybrid-sheet-close aria-label="Close">×</button>
      </div>
      <div class="size-hybrid-sheet__body">
        <div class="size-hybrid-sheet__list">${groupsHtml}</div>
      </div>
      <div class="size-hybrid-sheet__footer">
        <button type="button" class="size-hybrid-sheet__cta" data-size-hybrid-sheet-cta></button>
      </div>
    </div>
  `;

  document.body.appendChild(sheet);
  document.body.style.overflow = 'hidden';
  requestAnimationFrame(() => sheet.classList.add('is-open'));

  __activeSizeHybridSheet = sheet;

  let pendingKey = selectedKey;

  const syncSheetState = () => {
    const itemButtons = sheet.querySelectorAll('[data-size-hybrid-sheet-item]');
    itemButtons.forEach((button) => {
      button.classList.toggle('is-selected', button.dataset.sizeHybridSheetItem === pendingKey);
    });

    const selectedItem = itemsByKey.get(pendingKey);
    const cta = sheet.querySelector('[data-size-hybrid-sheet-cta]');
    if (!selectedItem || !cta) return;

    cta.textContent = `${translations.hybridSelectSize} ${formatDualSize(selectedItem.width, selectedItem.height, unit)} - ${selectedItem.formattedPrice}`;
    cta.disabled = !selectedItem.available;
  };

  syncSheetState();

  sheet.addEventListener('click', (event) => {
    const closeTarget = event.target.closest('[data-size-hybrid-sheet-close]');
    if (closeTarget) {
      event.preventDefault();
      closeHybridSizeSheet();
      return;
    }

    const itemTarget = event.target.closest('[data-size-hybrid-sheet-item]');
    if (itemTarget) {
      event.preventDefault();
      pendingKey = itemTarget.dataset.sizeHybridSheetItem;
      syncSheetState();
      return;
    }

    const cta = event.target.closest('[data-size-hybrid-sheet-cta]');
    if (!cta || cta.disabled) return;

    event.preventDefault();
    selectSizeInOptionContainer(container, pendingKey);
    closeHybridSizeSheet();
    setTimeout(() => {
      refreshHybridSelectorAndAdvice(container, getCurrentSizeUnit(container));
    }, 120);
  });

  __sizeHybridSheetKeyHandler = (event) => {
    if (event.key !== 'Escape') return;
    closeHybridSizeSheet();
  };
  document.addEventListener('keydown', __sizeHybridSheetKeyHandler);
}

function initMobileHybridSelectors() {
  const allOptionContainers = document.querySelectorAll('.m-product-option');
  allOptionContainers.forEach((container) => {
    if (!container.classList.contains('size-option-grid')) {
      cleanupNonSizeOptionContainer(container);
    }
  });

  const sizeContainers = document.querySelectorAll('.m-product-option.size-option-grid');
  if (!sizeContainers.length) {
    closeHybridSizeSheet();
    return;
  }

  sizeContainers.forEach((container) => {
    refreshHybridSelectorAndAdvice(container, getCurrentSizeUnit(container));
  });
}

/**
 * Store original cm values on elements
 */
function storeOriginalSizes(container) {
  // Store on swatch labels, tooltips, options, and custom dropdown options
  const elements = container.querySelectorAll('.m-product-option--node__label, .m-tooltip__content, option, .m-custom-select__option');
  elements.forEach(el => {
    const text = el.textContent.trim();
    const sizePair = parseSizePair(text);
    if (sizePair && !el.dataset.originalWidth) {
      el.dataset.originalWidth = String(sizePair.width);
      el.dataset.originalHeight = String(sizePair.height);
    }
  });
  
  // Also store on the selected label in the header
  const selectedLabel = container.querySelector('.option-label--selected');
  if (selectedLabel) {
    const text = selectedLabel.textContent.trim();
    const sizePair = parseSizePair(text);
    if (sizePair) {
      selectedLabel.dataset.originalWidth = String(sizePair.width);
      selectedLabel.dataset.originalHeight = String(sizePair.height);
    }
  }
  
  // Store on custom select trigger value
  const customSelectValue = container.querySelector('.m-custom-select__value');
  if (customSelectValue) {
    const text = customSelectValue.textContent.trim();
    const sizePair = parseSizePair(text);
    if (sizePair) {
      customSelectValue.dataset.originalWidth = String(sizePair.width);
      customSelectValue.dataset.originalHeight = String(sizePair.height);
    }
  }
}

/**
 * Convert size labels between cm and inch
 */
function convertSizeLabels(container, unit) {
  if (!isSizeOptionContainer(container)) {
    cleanupNonSizeOptionContainer(container);
    return;
  }

  syncSelectedLabelOriginalSize(container);

  const sizes = collectUniqueSizes(container);
  const translations = getSizeAdvisorChoiceCopy();
  const showDescriptorInBaseOptions = window.matchMedia('(max-width: 767px)').matches;

  const elements = container.querySelectorAll('[data-original-width]');
  
  elements.forEach(el => {
    const widthCm = parseInt(el.dataset.originalWidth, 10);
    const heightCm = parseInt(el.dataset.originalHeight, 10);

    const dualSize = formatDualSize(widthCm, heightCm, unit);
    const descriptorKey = getSizeDescriptorKey({ width: widthCm, height: heightCm }, sizes);
    const descriptorLabel = getSizeDescriptorLabel(descriptorKey, translations);
    const visibleDescriptor = showDescriptorInBaseOptions ? descriptorLabel : '';
    
    if (el.tagName === 'OPTION') {
      el.textContent = visibleDescriptor ? `${dualSize} - ${visibleDescriptor}` : dualSize;
    } else if (el.classList.contains('m-tooltip__content')) {
      el.textContent = dualSize;
    } else if (el.classList.contains('option-label--selected')) {
      el.textContent = dualSize;
    } else if (el.classList.contains('m-custom-select__option')) {
      const span = el.querySelector('span:first-child');
      if (span) {
        span.textContent = visibleDescriptor ? `${dualSize} - ${visibleDescriptor}` : dualSize;
      }
    } else if (el.classList.contains('m-custom-select__value')) {
      el.textContent = visibleDescriptor ? `${dualSize} - ${visibleDescriptor}` : dualSize;
    } else if (el.classList.contains('m-product-option--node__label')) {
      el.innerHTML = `<span class="size-option-label__main">${dualSize}</span>${visibleDescriptor ? `<span class="size-option-label__sub">${visibleDescriptor}</span>` : ''}`;
    } else {
      el.textContent = dualSize;
    }
  });

  const selectedSize = getSelectedSizeFromContainer(container);
  const selectedHint = container.querySelector('.size-format-hint');
  if (selectedHint) {
    const descriptorKey = getSizeDescriptorKey(selectedSize, sizes);
    const descriptorLabel = getSizeDescriptorLabel(descriptorKey, translations);
    selectedHint.textContent = showDescriptorInBaseOptions ? (descriptorLabel || translations.widthHeight) : translations.widthHeight;
  }

  applyRecommendedSizeBadge(container);
  refreshHybridSelectorAndAdvice(container, unit);
  syncCartSizeUnitProperty(unit);
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeAdvisor);
} else {
  initSizeAdvisor();
}

// Also initialize on Shopify section render (for theme editor)
document.addEventListener('shopify:section:load', initSizeAdvisor);

// Re-init when variants change
document.addEventListener('variant:change', () => {
  setTimeout(() => {
    initSizeUnitSwitcher();
    initMobileHybridSelectors();
  }, 50);
});
document.addEventListener('change', (e) => {
  if (e.target.closest('[data-variant-picker]')) {
    // When a size is selected, update the selected label with correct unit
    setTimeout(() => {
      const optionContainer = e.target.closest('.m-product-option');
      if (optionContainer && isSizeOptionContainer(optionContainer)) {
        const switcher = optionContainer.querySelector('.size-unit-switcher');
        const activeBtn = switcher?.querySelector('.size-unit-btn.is-active');
        const currentUnit = activeBtn?.dataset.sizeUnit || 'cm';

        syncSelectedLabelOriginalSize(optionContainer);
        
        // Re-apply current unit
        convertSizeLabels(optionContainer, currentUnit);
      }

      initProductLayoutEnhancements();
    }, 50);
  }
});

// A frame change keeps the size the same, so the existing size handler ignores it.
// The tile prices belong to the selected frame, so rebuild them here.
document.addEventListener('change', (e) => {
  if (!e.target.closest('[data-variant-picker]')) return;

  const optionContainer = e.target.closest('.m-product-option');
  if (optionContainer && isSizeOptionContainer(optionContainer)) return;

  setTimeout(() => {
    initMobileHybridSelectors();
  }, 60);
});
